import clsx5, { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, createContext, useState, useEffect, useRef, useCallback, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useInView } from 'react-cool-inview';

var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/helper/tailwind.ts
var tailwind_exports = {};
__export(tailwind_exports, {
  cn: () => cn
});
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
function Button({ children, ...other }) {
  return /* @__PURE__ */ jsx("button", { type: "button", ...other, className: "border-none px-4 py-2", children });
}
Button.displayName = "Button";
var Select = ({
  className,
  prefix,
  isError = false,
  error,
  onChange,
  options,
  placeholder,
  value,
  withIcon = true
}) => {
  const [isOpenDropdown, setOpenDropdown] = useState(false);
  const [valueState, setValue] = useState(value);
  const [selectedLabel, setSelectedLabel] = useState(value);
  useEffect(() => {
    if (!value) return;
    setValue(value);
    setSelectedLabel(value);
  }, [value]);
  const handleChange = (label, value2) => {
    setSelectedLabel(label);
    setValue(value2);
    if (onChange) {
      onChange({ label, value: value2 });
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: tailwind_exports.cn("relative", className), children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: tailwind_exports.cn(
          "bg-white border border-solid flex items-center justify-between gap-2 rounded-md",
          {
            "border-neutral30": !isError,
            "border-borderError": isError
          }
        ),
        children: [
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: "flex items-center flex-auto",
              onClick: () => setOpenDropdown(!isOpenDropdown),
              children: [
                Boolean(prefix) && /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: tailwind_exports.cn(
                      "py-3 px-3 h-[42px] flex flex-none items-center rounded-tl-md rounded-bl-md border-0 border-r border-solid border-neutral30",
                      {
                        "bg-[#F4F4F6]": !isError,
                        "bg-[#ffeaed]": isError
                      }
                    ),
                    children: prefix
                  }
                ),
                /* @__PURE__ */ jsx("div", { className: "min-w-16 md:w-full line-clamp-1 flex-auto text-base leading-6 p-2", children: placeholder && (valueState === void 0 || valueState === "") ? placeholder : selectedLabel })
              ]
            }
          ),
          withIcon && /* @__PURE__ */ jsx(
            "svg",
            {
              xmlns: "http://www.w3.org/2000/svg",
              width: "21",
              height: "20",
              viewBox: "0 0 21 20",
              fill: "none",
              className: tailwind_exports.cn({
                "flex-none mr-2": true,
                "rotate-180": isOpenDropdown
              }),
              children: /* @__PURE__ */ jsx(
                "path",
                {
                  fillRule: "evenodd",
                  clipRule: "evenodd",
                  d: "M10.7336 13.1252C10.5736 13.1252 10.4136 13.0642 10.2916 12.9422L4.84961 7.50021L5.73361 6.61621L10.7336 11.6162L15.7336 6.61621L16.6176 7.50021L11.1756 12.9422C11.0536 13.0642 10.8936 13.1252 10.7336 13.1252Z",
                  fill: "#2980B9"
                }
              )
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: tailwind_exports.cn({
          "bg-transparent top-0 left-0 right-0 bottom-0 w-full h-full": true,
          "fixed z-1": isOpenDropdown,
          "none z-0": !isOpenDropdown
        }),
        onClick: () => setOpenDropdown(false)
      }
    ),
    /* @__PURE__ */ jsx(
      "ul",
      {
        className: tailwind_exports.cn({
          "absolute bg-white z-[2] w-[calc(100%-44px)] left-11 top-12 list-none border border-solid border-neutral30 rounded-md transition-all duration-100 ease-in-out overflow-scroll max-h-[240px]": true,
          "h-auto opacity-100": isOpenDropdown,
          "h-0 opacity-0": !isOpenDropdown
        }),
        children: options.map(({ label, value: value2 }) => /* @__PURE__ */ jsx(
          "li",
          {
            className: "px-4 py-3 text-[#0d1a35] text-sm leading-normal cursor-pointer hover:bg-[#F4F4F6]",
            onClick: () => {
              setOpenDropdown(false);
              handleChange(label, value2);
            },
            children: label
          }
        ))
      }
    ),
    isError && /* @__PURE__ */ jsx("div", { className: "text-errorAlert text-xs mt-2 text-left", children: error })
  ] });
};
var Input = forwardRef(
  ({
    containerClassName,
    isError,
    errorText,
    prefix,
    suffix,
    alignment = "left",
    className,
    prefixClassName,
    suffixClassName,
    value,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: tailwind_exports.cn(
          "text-accent text-sm",
          containerClassName
        ),
        children: [
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: tailwind_exports.cn(
                "border border-solid flex flex-grow items-stretch rounded-md",
                {
                  "border-neutral30": !isError,
                  "border-borderError": isError
                }
              ),
              children: [
                Boolean(prefix) && /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: tailwind_exports.cn(
                      "py-2 px-3 flex items-center rounded-tl-md rounded-bl-md",
                      {
                        "bg-[#F4F4F6]": !isError,
                        "bg-[#ffeaed]": isError
                      },
                      prefixClassName
                    ),
                    children: prefix
                  }
                ),
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    ...props,
                    value: value ?? "",
                    ref,
                    className: tailwind_exports.cn(
                      "[appearance:textfield] border-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                      "py-2 px-3 border-none outline-none w-full rounded-md placeholder:text-disabledText",
                      className,
                      {
                        "text-right": alignment === "right"
                      }
                    ),
                    type: props.type || "text"
                  }
                ),
                Boolean(suffix) && /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: tailwind_exports.cn(
                      "py-2 px-3 bg-[#F4F4F6] flex items-center rounded-tr-md rounded-br-md",
                      { "bg-[#F4F4F6]": !isError, "bg-[#ffeaed]": isError },
                      suffixClassName
                    ),
                    children: suffix
                  }
                )
              ]
            }
          ),
          isError && errorText && /* @__PURE__ */ jsx("span", { className: "mt-[1px] text-xs text-borderError", children: errorText })
        ]
      }
    );
  }
);
var Label = ({ className, ...props }) => {
  return /* @__PURE__ */ jsx(
    "label",
    {
      className: clsx5("text-accent text-sm font-medium mb-1", className),
      ...props
    }
  );
};
var LabelWithFlagSuccess = ({ children, valid }) => {
  return /* @__PURE__ */ jsxs(Label, { className: "flex items-center justify-between", children: [
    /* @__PURE__ */ jsx("span", { children }),
    /* @__PURE__ */ jsxs(
      "svg",
      {
        width: "16",
        height: "16",
        viewBox: "0 0 16 16",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        children: [
          /* @__PURE__ */ jsxs("g", { clipPath: "url(#clip0_20163_43514)", children: [
            /* @__PURE__ */ jsxs(
              "mask",
              {
                id: "mask0_20163_43514",
                style: { maskType: "luminance" },
                maskUnits: "userSpaceOnUse",
                x: "0",
                y: "0",
                width: "16",
                height: "16",
                children: [
                  /* @__PURE__ */ jsx(
                    "path",
                    {
                      d: "M7.99968 14.6663C8.87532 14.6675 9.74256 14.4956 10.5515 14.1605C11.3605 13.8253 12.0953 13.3337 12.7137 12.7137C13.3337 12.0953 13.8253 11.3605 14.1605 10.5515C14.4956 9.74256 14.6675 8.87532 14.6663 7.99968C14.6675 7.12404 14.4955 6.25681 14.1604 5.44782C13.8253 4.63884 13.3337 3.90405 12.7137 3.28568C12.0953 2.66569 11.3605 2.17401 10.5515 1.83891C9.74256 1.5038 8.87532 1.33188 7.99968 1.33301C7.12404 1.3319 6.25681 1.50383 5.44782 1.83893C4.63884 2.17404 3.90405 2.6657 3.28568 3.28568C2.6657 3.90405 2.17404 4.63884 1.83893 5.44782C1.50383 6.25681 1.3319 7.12404 1.33301 7.99968C1.33188 8.87532 1.5038 9.74256 1.83891 10.5515C2.17401 11.3605 2.66569 12.0953 3.28568 12.7137C3.90405 13.3337 4.63884 13.8253 5.44782 14.1604C6.25681 14.4955 7.12404 14.6675 7.99968 14.6663Z",
                      fill: "white",
                      stroke: "white",
                      strokeLinejoin: "round"
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    "path",
                    {
                      d: "M5.33301 8L7.33301 10L11.333 6",
                      stroke: "black",
                      strokeLinecap: "round",
                      strokeLinejoin: "round"
                    }
                  )
                ]
              }
            ),
            /* @__PURE__ */ jsx("g", { mask: "url(#mask0_20163_43514)", children: /* @__PURE__ */ jsx("path", { d: "M0 0H16V16H0V0Z", fill: valid ? "#38BAA7" : "#CED3D7" }) })
          ] }),
          /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx("clipPath", { id: "clip0_20163_43514", children: /* @__PURE__ */ jsx("rect", { width: "16", height: "16", fill: "white" }) }) })
        ]
      }
    )
  ] });
};
var Slider = ({
  min = 1,
  max = 100,
  value = max / 2,
  style = {},
  ...props
}) => {
  const percentage = (value - min) / (max - min) * 100;
  return /* @__PURE__ */ jsx("div", { className: "h-6", children: /* @__PURE__ */ jsx(
    "input",
    {
      type: "range",
      min,
      max,
      value,
      ...props,
      className: `
          w-full appearance-none bg-transparent cursor-pointer accent-[#2951a3]
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6
          [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[#CFD1D7]
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:shadow-[0_2px_4px_rgba(96,97,112,0.16),0_0_0.5px_rgba(40,41,61,0.04)]
          [&::-webkit-slider-thumb]:-mt-[10.25px] [&::-webkit-slider-thumb]:cursor-pointer
          [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:h-1
          [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[#CFD1D7]
          [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:rounded-full
          [&::-moz-range-thumb]:shadow-[0_2px_4px_rgba(96,97,112,0.16),0_0_0.5px_rgba(40,41,61,0.04)]
          [&::-moz-range-thumb]:-mt-[10.25px] [&::-moz-range-thumb]:cursor-pointer
          [&::-moz-range-track]:rounded-full [&::-moz-range-track]:h-1
        `,
      style: {
        background: `linear-gradient(to right, rgb(41, 81, 163) ${percentage}%, #E7E8EB ${percentage}%, #E7E8EB 100%)`,
        ...style
      }
    }
  ) });
};
function Dialog({
  open,
  onClose,
  children,
  className = "",
  parentClassName = "",
  contentClassName = "",
  style,
  ...props
}) {
  const [isShowModal, setIsShowModal] = useState(false);
  useEffect(() => {
    if (open) {
      document.documentElement.style.overflow = "hidden";
      setIsShowModal(true);
    } else {
      document.documentElement.style.overflow = "";
      setIsShowModal(false);
    }
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [open]);
  if (!open) return null;
  return /* @__PURE__ */ jsxs(
    "dialog",
    {
      ...props,
      className: cn(
        "bg-transparent fixed z-[999] inset-0 w-full h-screen md:flex md:justify-center md:items-center border-0 r123-dialog",
        parentClassName
      ),
      open: true,
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              {
                "md:animate-fadingModal animate-slidingUpModal": isShowModal
              },
              "overflow-y-auto md:relative md:rounded-2xl md:max-w-none max-w-full fixed md:bottom-auto bottom-0 rounded-t-2xl z-[99] shadow-[0_1px_2px_0_rgba(0, 0, 0, 0.2)] md:w-[520px] w-full p-6 bg-white",
              "max-h-[calc(100vh-1rem)] r123-dialog-wrap no-scrollbar",
              className
            ),
            style,
            children: /* @__PURE__ */ jsx("div", { className: contentClassName, children })
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            className: "fixed inset-0 opacity-60 bg-black border-0 outline-none w-full h-screen top-0 left-0 right-0 bottom-0",
            onClick: (e) => {
              e.stopPropagation();
              if (onClose) {
                onClose();
              }
            },
            type: "button",
            children: "\xA0"
          }
        )
      ]
    }
  );
}
var DialogSuccess = ({
  title,
  subTitle,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsxs(
    Dialog,
    {
      ...props,
      className: cn(
        "w-[450px] bg-[#2951a3] !p-8 h-[508px] flex justify-center items-center text-center",
        className
      ),
      children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            onClick: () => props.onClose?.(),
            className: "cursor-pointer absolute right-6 top-6 border-none outline-none bg-transparent",
            children: /* @__PURE__ */ jsx(
              "svg",
              {
                height: "16px",
                viewBox: "0 0 16 16",
                width: "16px",
                xmlns: "http://www.w3.org/2000/svg",
                children: /* @__PURE__ */ jsx("g", { transform: "translate(0, 0)", children: /* @__PURE__ */ jsx(
                  "path",
                  {
                    fill: "#fff",
                    d: "M9.88546 8.00017L15.6093 2.27629C16.1307 1.75496 16.1307 0.912314 15.6093 0.390992C15.088 -0.130331 14.2454 -0.130331 13.724 0.390992L8.00017 6.11487L2.27629 0.390992C1.75496 -0.130331 0.912314 -0.130331 0.390992 0.390992C-0.130331 0.912314 -0.130331 1.75496 0.390992 2.27629L6.11487 8.00017L0.390992 13.724C-0.130331 14.2454 -0.130331 15.088 0.390992 15.6093C0.650986 15.8693 0.992313 16 1.33364 16C1.67497 16 2.01629 15.8693 2.27629 15.6093L8.00017 9.88546L13.724 15.6093C13.984 15.8693 14.3254 16 14.6667 16C15.008 16 15.3493 15.8693 15.6093 15.6093C16.1307 15.088 16.1307 14.2454 15.6093 13.724L9.88546 8.00017Z"
                  }
                ) })
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "ui-molecules-callback-dialog__wrapper", children: [
          /* @__PURE__ */ jsxs(
            "svg",
            {
              className: "mx-auto",
              width: "104",
              height: "104",
              viewBox: "0 0 104 104",
              fill: "none",
              xmlns: "http://www.w3.org/2000/svg",
              children: [
                /* @__PURE__ */ jsx("rect", { width: "104", height: "104", fill: "url(#pattern0)" }),
                /* @__PURE__ */ jsxs("defs", { children: [
                  /* @__PURE__ */ jsx(
                    "pattern",
                    {
                      id: "pattern0",
                      patternContentUnits: "objectBoundingBox",
                      width: "1",
                      height: "1",
                      children: /* @__PURE__ */ jsx(
                        "use",
                        {
                          xlinkHref: "#image0_4476_13452",
                          transform: "scale(0.00666667)"
                        }
                      )
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    "image",
                    {
                      id: "image0_4476_13452",
                      width: "150",
                      height: "150",
                      xlinkHref: "data:image/png;base64,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"
                    }
                  )
                ] })
              ]
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "mt-6 text-white", children: [
            !!title && /* @__PURE__ */ jsx("p", { className: "text-2xl leading-8 font-bold mb-0", children: title }),
            !!subTitle && /* @__PURE__ */ jsx("p", { className: "mt-4 font-medium text-sm leading-6 mb-0", children: subTitle })
          ] })
        ] })
      ]
    }
  );
};
var DialogWithHeader = ({
  open,
  onClose,
  image,
  title,
  children,
  className,
  parentClassName,
  preventCloseOnBackground,
  contentClassName,
  titleClassName
}) => {
  return /* @__PURE__ */ jsxs(
    Dialog,
    {
      open,
      onClose: preventCloseOnBackground ? void 0 : onClose,
      parentClassName,
      className: `${image ? "p-0 no-scrollbar" : ""} ${className}`,
      contentClassName,
      children: [
        image && /* @__PURE__ */ jsx("div", { className: "w-full h-[150px] sticky top-0 z-[999]", children: /* @__PURE__ */ jsx(
          "img",
          {
            src: image,
            alt: title,
            className: "w-full h-full object-cover object-center"
          }
        ) }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: cn(titleClassName || "", {
              "flex justify-between items-center pb-4 border-0 border-b border-b-[#e7e8eb] mb-4 border-solid": true,
              "px-4 py-6 md:p-6": image
            }),
            children: [
              /* @__PURE__ */ jsx("p", { className: "font-bold text-base m-0", children: title }),
              /* @__PURE__ */ jsx(
                "button",
                {
                  className: "outline-none border-none flex cursor-pointer bg-transparent",
                  onClick: (e) => {
                    e.stopPropagation();
                    if (onClose) {
                      onClose();
                    }
                  },
                  children: /* @__PURE__ */ jsx(
                    "svg",
                    {
                      role: "button",
                      height: "16px",
                      viewBox: "0 0 16 16",
                      width: "16px",
                      xmlns: "http://www.w3.org/2000/svg",
                      children: /* @__PURE__ */ jsx("g", { transform: "translate(0, 0)", fill: "#697684", children: /* @__PURE__ */ jsx("path", { d: "M9.88546 8.00017L15.6093 2.27629C16.1307 1.75496 16.1307 0.912314 15.6093 0.390992C15.088 -0.130331 14.2454 -0.130331 13.724 0.390992L8.00017 6.11487L2.27629 0.390992C1.75496 -0.130331 0.912314 -0.130331 0.390992 0.390992C-0.130331 0.912314 -0.130331 1.75496 0.390992 2.27629L6.11487 8.00017L0.390992 13.724C-0.130331 14.2454 -0.130331 15.088 0.390992 15.6093C0.650986 15.8693 0.992313 16 1.33364 16C1.67497 16 2.01629 15.8693 2.27629 15.6093L8.00017 9.88546L13.724 15.6093C13.984 15.8693 14.3254 16 14.6667 16C15.008 16 15.3493 15.8693 15.6093 15.6093C16.1307 15.088 16.1307 14.2454 15.6093 13.724L9.88546 8.00017Z" }) })
                    }
                  )
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx("div", { className: image ? "px-4 pb-6 md:p-6 md:pt-0" : "", children })
      ]
    }
  );
};
var useAsyncModal = function() {
  const resolver = useRef();
  const [data, setData] = useState();
  const [isShowModal, setShowModal] = useState(false);
  const open = useCallback(async (data2) => {
    setData(data2 ?? {});
    setShowModal(true);
    return new Promise(
      (resolve) => resolver.current = resolve
    );
  }, []);
  const close = useCallback((value) => {
    setShowModal(false);
    setData(void 0);
    resolver.current?.(value);
  }, []);
  const modal = {
    open: isShowModal,
    onClose: () => close()
  };
  useEffect(() => () => close(), [close]);
  useEffect(() => {
    const handleBeforeUnload = () => {
      resolver.current?.(void 0);
      close();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);
  const updateData = (updatedData) => {
    setData((prevData) => {
      if (!prevData) return updatedData;
      return { ...prevData, ...updatedData };
    });
  };
  return { modal, data, open, close, updateData };
};

// src/helper/cookies.ts
function setCookie(key, value, options = {}) {
  if (typeof document === "undefined") return;
  let cookieString = `${key}=${value}`;
  if (options.maxAge) cookieString += `; Max-Age=${options.maxAge}`;
  if (options.expires) cookieString += `; Expires=${options.expires}`;
  cookieString += `; Path=${options.path || "/"}`;
  if (options.domain) cookieString += `; Domain=${options.domain}`;
  if (options.secure) cookieString += "; Secure";
  if (options.sameSite) cookieString += `; SameSite=${options.sameSite}`;
  document.cookie = cookieString;
}
function getCookie(name) {
  if (typeof document === "undefined") return;
  const nameLenPlus = name.length + 1;
  return document.cookie.split(";").map((c) => c.trim()).filter((cookie) => {
    return cookie.substring(0, nameLenPlus) === `${name}=`;
  }).map((cookie) => {
    try {
      return decodeURIComponent(cookie.substring(nameLenPlus));
    } catch (err) {
      try {
        return decodeURIComponent(
          encodeURIComponent(cookie.substring(nameLenPlus))
        );
      } catch (err2) {
        return cookie.substring(nameLenPlus);
      }
    }
  })[0] || void 0;
}
function deleteCookie(key, options = {}) {
  setCookie(key, "", { ...options, maxAge: -1 });
}

// src/hooks/useCookiesState.ts
var ONE_MONTH_IN_MS = 30 * 24 * 60 * 60 * 1e3;
var useCookiesState = (key, defaultValue, expires = ONE_MONTH_IN_MS, { serialize = JSON.stringify, deserialize = JSON.parse } = {}) => {
  const isFirstRender = useRef(true);
  const initDefaultValue = () => {
    const data = getCookie(key);
    if (data) {
      return deserialize(data);
    } else {
      deleteCookie(key);
      return defaultValue;
    }
  };
  const [state, setState] = useState(initDefaultValue);
  useEffect(() => {
    const data = getCookie(key);
    data ? setState(deserialize(data)) : deleteCookie(key);
  }, []);
  const updateCookie = () => {
    const data = getCookie(key);
    data ? setState(deserialize(data)) : deleteCookie(key);
  };
  const prevKeyRef = useRef(key);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const prevKey = prevKeyRef.current;
    if (prevKey !== key) {
      deleteCookie(key);
    }
    prevKeyRef.current = key;
    setCookie(key, serialize(state), {
      expires: new Date(Date.now() + expires),
      sameSite: "strict"
    });
  }, [key, state]);
  return [state, setState, updateCookie];
};

// src/helper/encrypData.ts
var encryptData = async ({
  host,
  data,
  path = "/portal-api/secret/"
}) => {
  return fetch(`${host}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      action: "ENCRYPT",
      data
    })
  }).then((response) => response.json()).then(({ data: data2 }) => data2).catch((err) => {
    console.debug("[secret-error]", err);
    throw err;
  });
};
var decryptData = async ({
  host,
  data,
  path = "/portal-api/secret/"
}) => {
  return fetch(`${host}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      action: "DECRYPT",
      data
    })
  }).then((response) => response.json()).then(({ data: data2 }) => data2).catch((err) => {
    console.debug("[secret-error]", err);
    throw err;
  });
};

// src/hooks/useCookieStateWithEncryption.ts
var ONE_MONTH_IN_MS2 = 30 * 24 * 60 * 60 * 1e3;
var useCookieStateWithEncryption = (key, defaultValue, expires = ONE_MONTH_IN_MS2, { host = "http://localhost:3000", path = "/portal-api/secret" } = {}) => {
  const [state, setState] = useState(defaultValue);
  const prevKeyRef = useRef(key);
  useEffect(() => {
    const initState = async () => {
      const data = getCookie(key);
      if (data) {
        try {
          const decryptedData = await decryptData({
            host,
            path,
            data
          });
          setState(decryptedData);
        } catch (error) {
          console.debug(`[cookie-decrypt-error] ${key}:`, error);
          setState(defaultValue);
        }
      } else {
        setState(defaultValue);
        updateState(defaultValue);
      }
    };
    initState();
  }, [key, defaultValue, host, path]);
  const updateState = async (newValue) => {
    try {
      const prevKey = prevKeyRef.current;
      if (prevKey !== key) {
        deleteCookie(prevKey);
        prevKeyRef.current = key;
      }
      const encryptedData = await encryptData({
        host,
        path,
        data: newValue
      });
      setCookie(key, encryptedData, {
        expires: new Date(Date.now() + expires),
        sameSite: "strict"
      });
      setState(newValue);
    } catch (error) {
      console.debug(`[cookie-encrypt-error] ${key}:`, error);
      deleteCookie(key);
    }
  };
  const updateStateWithModifier = (stateModifier) => {
    return new Promise((resolve) => {
      setState((prevState) => {
        const newValue = stateModifier(prevState);
        resolve({ ...newValue });
        return newValue;
      });
    });
  };
  const updateStateWithCallbackModifier = async (stateModifier) => {
    try {
      const prevKey = prevKeyRef.current;
      if (prevKey !== key) {
        deleteCookie(prevKey);
        prevKeyRef.current = key;
      }
      const data = await updateStateWithModifier(stateModifier);
      if (!data) {
        return;
      }
      const encryptedData = await encryptData({
        host,
        path,
        data
      });
      setCookie(key, encryptedData, {
        expires: new Date(Date.now() + expires),
        sameSite: "strict"
      });
    } catch (error) {
      console.debug(`[cookie-encrypt-error] ${key}:`, error);
      deleteCookie(key);
    }
  };
  return { state, updateState, updateStateWithCallbackModifier };
};
function useIntersectionObserver(callback, options) {
  const ref = useRef(null);
  const [entry, setEntry] = useState();
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry2]) => {
      setEntry(entry2);
      if (callback) callback(entry2);
    }, options);
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [ref, options, callback]);
  return [ref, entry];
}
var useMediaQuery = (query, initialValue) => {
  const getMatches = (query2) => {
    if (typeof window !== "undefined") {
      return window.matchMedia(query2).matches;
    }
    return void 0;
  };
  const [matches, setMatches] = useState(
    initialValue ?? getMatches(query)
  );
  function handleChange() {
    setMatches(getMatches(query));
  }
  useEffect(() => {
    const matchMedia = window.matchMedia(query);
    handleChange();
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener("change", handleChange);
    }
    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener("change", handleChange);
      }
    };
  }, [query]);
  return matches;
};
var useOnClickOutside = (ref, handler, exceptions) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      const isException = exceptions?.some(
        (exceptionRef) => exceptionRef?.current && exceptionRef.current.contains(event.target)
      );
      if (isException) {
        return;
      }
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
};
function useSlideOverflowX(ref) {
  const isMouseDown = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const scrollSpeed = 2;
  const onMouseLeaving = (e) => {
    e.preventDefault();
    isMouseDown.current = false;
    if (!ref.current) return;
    ref.current.style.scrollSnapType = "";
    setPointerEventChildren("");
  };
  const onTouchEnd = (e) => {
    e.preventDefault();
    isMouseDown.current = false;
    if (!ref.current) return;
    ref.current.style.scrollSnapType = "";
    setPointerEventChildren("");
  };
  const setCurrentStart = (pageX) => {
    if (!ref.current) return;
    isMouseDown.current = true;
    startX.current = pageX - ref.current.offsetLeft;
    scrollLeft.current = ref.current.scrollLeft;
    ref.current.style.scrollSnapType = "none";
  };
  const moveScroll = (pageX) => {
    if (!ref.current || !isMouseDown.current) return;
    ref.current.scrollLeft = scrollLeft.current - (pageX - ref.current.offsetLeft - startX.current) * scrollSpeed;
  };
  const onMouseMove = (e) => {
    e.preventDefault();
    moveScroll(e.pageX);
  };
  const onTouchMove = (e) => {
    moveScroll(e.touches[0].pageX);
  };
  const onMouseDown = (e) => {
    setCurrentStart(e.pageX);
    if (!ref.current) return;
  };
  const setPointerEventChildren = (value) => {
    if (!ref.current) return;
    const children = ref.current.children;
    if (!children.length) return;
    for (let i = 0; i < children.length; i++) {
      const item = children[i];
      item.style.pointerEvents = value;
    }
  };
  const onTouchStart = (e) => {
    setCurrentStart(e.touches[0].pageX);
  };
  return {
    onMouseLeaving,
    onTouchEnd,
    onMouseMove,
    onMouseDown,
    onTouchMove,
    onTouchStart
  };
}
var useStorageState = (key, defaultValue) => {
  const [state, setState] = useState(defaultValue);
  useEffect(() => {
    const initDefaultValue = async () => {
      const data = localStorage.getItem(key);
      if (data) {
        setState(JSON.parse(data));
      } else {
        setState(defaultValue);
        updateState(defaultValue);
      }
    };
    initDefaultValue();
  }, [key]);
  const updateState = async (newValue) => {
    localStorage.setItem(key, JSON.stringify(newValue));
    setState(newValue);
  };
  return { state, updateState };
};
var useStorageStateWithEncryption = (key, defaultValue, { host = "http://localhost:3000", path = "/portal-api/secret" } = {}) => {
  const [state, setState] = useState(defaultValue);
  useEffect(() => {
    const initDefaultValue = async () => {
      const data = localStorage.getItem(key);
      if (data) {
        try {
          const decryptedData = await decryptData({
            host,
            path,
            data
          });
          setState(decryptedData);
        } catch (error) {
          console.error(`[storage-decrypt-error] ${key}:`, error);
          setState(defaultValue);
        }
      } else {
        setState(defaultValue);
        updateState(defaultValue);
      }
    };
    initDefaultValue();
  }, [key, defaultValue, host, path]);
  const updateState = async (newValue) => {
    try {
      const encryptedData = await encryptData({
        host,
        path,
        data: newValue
      });
      localStorage.setItem(key, encryptedData);
      setState(newValue);
    } catch (error) {
      console.error(`[storage-encrypt-error] ${key}:`, error);
    }
  };
  const updateStateWithModifier = (stateModifier) => {
    return new Promise((resolve) => {
      setState((prevState) => {
        const newValue = stateModifier(prevState);
        resolve({ ...newValue });
        return newValue;
      });
    });
  };
  const updateStateWithCallbackModifier = async (stateModifier) => {
    try {
      const data = await updateStateWithModifier(stateModifier);
      if (!data) {
        return;
      }
      const encryptedData = await encryptData({
        host,
        path,
        data
      });
      localStorage.setItem(key, encryptedData);
    } catch (error) {
      console.error(`[storage-encrypt-error] ${key}:`, error);
    }
  };
  return { state, updateState, updateStateWithCallbackModifier };
};
var ReactPortalComponent = ({
  children,
  wrapperId
}) => {
  if (typeof document === "undefined") return null;
  const wrapper = document.getElementById(wrapperId || "");
  return /* @__PURE__ */ jsx(Fragment, { children: createPortal(children, wrapper || document.body) });
};
var ChipSelection = ({
  className,
  isActive,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "button",
      className: tailwind_exports.cn(
        "py-1 px-2 border border-solid font-light text-sm cursor-pointer rounded-lg",
        {
          "bg-blueBase text-white border-transparent": isActive,
          "bg-white border-greyText text-greyText": !isActive
        },
        className
      ),
      ...props
    }
  );
};
var ChipSelection_default = ChipSelection;
var Loader = ({ className }) => {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn(
        "animate-spin block mx-auto w-6 aspect-square rounded-full border-[3px] border-solid border-[#8da1d7] border-r-white",
        className
      )
    }
  );
};
var loader_default = Loader;
var CheckboxContext = createContext({
  name: "",
  onChange: () => {
  },
  value: []
});
var Checkbox = ({
  name,
  onChange,
  value = [],
  children
}) => {
  const onChangeItem = (response) => {
    if (value.includes(response)) {
      onChange(
        value.filter((item) => item !== response)
      );
    } else {
      onChange([...value, response]);
    }
  };
  const contextValue = {
    name,
    onChange: onChangeItem,
    value
  };
  return /* @__PURE__ */ jsx(CheckboxContext.Provider, { value: contextValue, children });
};
var Checkbox_default = Checkbox;
var CheckboxItem = ({
  id,
  label,
  value,
  tabIndex = 0,
  className,
  size = 16,
  labelOnLeft = false,
  dataTestId
}) => {
  const { name, onChange, value: contextValue } = useContext(CheckboxContext);
  return /* @__PURE__ */ jsxs("div", { tabIndex, children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        id,
        name,
        type: "checkbox",
        className: "hidden",
        value,
        checked: contextValue?.includes(value),
        onChange: () => onChange(value),
        "data-test-id": dataTestId || ""
      }
    ),
    /* @__PURE__ */ jsxs(
      "label",
      {
        className: "flex items-start gap-2 cursor-pointer",
        htmlFor: id,
        tabIndex,
        children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                {
                  "border border-solid rounded-lg w-4 h-4 cursor-pointer shrink-0 relative top-1": true,
                  "border-neutral50 bg-white": !contextValue?.includes(value),
                  "border-primary bg-transparent rounded-md border-none": contextValue?.includes(value),
                  "order-2": labelOnLeft
                },
                className
              ),
              children: contextValue?.includes(value) && /* @__PURE__ */ jsx(
                "svg",
                {
                  className: "rounded-md",
                  xmlns: "http://www.w3.org/2000/svg",
                  width: size,
                  height: size,
                  viewBox: "0 0 16 16",
                  fill: "none",
                  children: /* @__PURE__ */ jsx(
                    "path",
                    {
                      fillRule: "evenodd",
                      clipRule: "evenodd",
                      d: "M0 1.0009C0 0.448312 0.444566 0 1.0009 0H14.9991C15.5517 0 16 0.444566 16 1.0009V14.9991C16 15.5517 15.5554 16 14.9991 16H1.0009C0.448312 16 0 15.5554 0 14.9991V1.0009ZM5.75062 11.5824L6.22204 12.0538L13.3601 4.91578C13.6204 4.65541 13.6204 4.23332 13.3601 3.97295C13.0997 3.71258 12.6776 3.71258 12.4172 3.97295L6.22204 10.2218L3.58212 7.52823C3.32175 7.26785 2.89966 7.26785 2.63929 7.52823C2.37892 7.7886 2.37892 8.21068 2.63929 8.47106L5.75062 11.5824Z",
                      fill: "#2951A3"
                    }
                  )
                }
              )
            }
          ),
          label && /* @__PURE__ */ jsx(
            "span",
            {
              className: cn("text-sm cursor-pointer", {
                "order-1": labelOnLeft
              }),
              children: label
            }
          )
        ]
      }
    )
  ] });
};
var CheckboxItem_default = CheckboxItem;
var ScrollableWrapper = ({
  children,
  className,
  ...props
}) => {
  const containerRef = useRef(null);
  const { onMouseLeaving, onMouseDown, onMouseMove } = useSlideOverflowX(containerRef);
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "no-scrollbar flex overflow-x-auto snap-mandatory snap-x scroll-pl-4 gap-4 [&>*]:-scroll-ml-4 [&>*]:snap-start [&>*]:shrink-0",
        className
      ),
      ref: containerRef,
      ...{
        onMouseLeave: onMouseLeaving,
        onMouseUp: onMouseLeaving,
        onMouseMove,
        onMouseDown
      },
      ...props,
      children
    }
  );
};
var ScrollableWrapper_default = ScrollableWrapper;
var ChipOfficialIcon = ({ className }) => {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "w-[133px] whitespace-pre relative bg-[#3485ff] text-white py-1 pl-4 pr-2 ml-[10px] text-xs font-medium rounded-r-2xl",
        className
      ),
      children: [
        /* @__PURE__ */ jsx(
          "svg",
          {
            width: "24",
            height: "24",
            viewBox: "0 0 24 24",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            className: "absolute top-0 left-0 -translate-x-1/2",
            children: /* @__PURE__ */ jsxs("g", { id: "official-dev-mark", children: [
              /* @__PURE__ */ jsx(
                "path",
                {
                  fillRule: "evenodd",
                  clipRule: "evenodd",
                  d: "M9.74872 0.829227C10.3767 0.293986 11.1749 0 12 0C12.8251 0 13.6233 0.293986 14.2513 0.829227L15.7217 2.08328C15.9072 2.24151 16.138 2.33706 16.3811 2.3563L18.3073 2.51016C19.1294 2.57599 19.9012 2.93243 20.4844 3.51561C21.0676 4.0988 21.424 4.87061 21.4898 5.69272L21.6437 7.62008C21.6632 7.86276 21.7587 8.09311 21.9167 8.27834L23.1708 9.74872C23.706 10.3767 24 11.1749 24 12C24 12.8251 23.706 13.6233 23.1708 14.2513L21.9167 15.7217C21.7585 15.9072 21.6629 16.138 21.6437 16.3811L21.4898 18.3073C21.424 19.1294 21.0676 19.9012 20.4844 20.4844C19.9012 21.0676 19.1294 21.424 18.3073 21.4898L16.3799 21.6437C16.1372 21.6632 15.9069 21.7587 15.7217 21.9167L14.2513 23.1708C13.6233 23.706 12.8251 24 12 24C11.1749 24 10.3767 23.706 9.74872 23.1708L8.27834 21.9167C8.0928 21.7585 7.86201 21.6629 7.61892 21.6437L5.69272 21.4898C4.87061 21.424 4.0988 21.0676 3.51561 20.4844C2.93243 19.9012 2.57599 19.1294 2.51016 18.3073L2.3563 16.3799C2.33683 16.1372 2.24128 15.9069 2.08328 15.7217L0.829227 14.2513C0.293986 13.6233 0 12.8251 0 12C0 11.1749 0.293986 10.3767 0.829227 9.74872L2.08328 8.27834C2.24151 8.0928 2.33706 7.86201 2.3563 7.61892L2.51016 5.69272C2.57599 4.87061 2.93243 4.0988 3.51561 3.51561C4.0988 2.93243 4.87061 2.57599 5.69272 2.51016L7.62008 2.3563C7.86276 2.33683 8.09311 2.24128 8.27834 2.08328L9.74872 0.829227Z",
                  fill: "#FECF04"
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M17 9.32451C17.0028 9.67483 16.8764 10.0123 16.648 10.2643L11.6326 15.6088C11.3975 15.8593 11.0786 16 10.7461 16C10.4137 16 10.0948 15.8593 9.85967 15.6088L7.35197 12.9366C7.12357 12.6846 6.99719 12.3471 7.00005 11.9967C7.0029 11.6464 7.13477 11.3113 7.36724 11.0636C7.59972 10.8158 7.9142 10.6753 8.24296 10.6723C8.57171 10.6692 8.88844 10.8039 9.12492 11.0473L10.7461 12.7749L14.8751 8.37506C15.1116 8.13168 15.4283 7.99701 15.757 8.00005C16.0858 8.00309 16.4003 8.14361 16.6328 8.39134C16.8652 8.63907 16.9971 8.97418 17 9.32451Z",
                  fill: "#2951A3"
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  fillRule: "evenodd",
                  clipRule: "evenodd",
                  d: "M8.27834 21.9167L9.74872 23.1708C10.3767 23.706 11.1749 24 12 24C12.8251 24 13.6233 23.706 14.2513 23.1708L15.7217 21.9167C15.9069 21.7587 16.1372 21.6632 16.3799 21.6437L18.3073 21.4898C19.1294 21.424 19.9012 21.0676 20.4844 20.4844C21.0676 19.9012 21.424 19.1294 21.4898 18.3073L21.6437 16.3811C21.6629 16.138 21.7585 15.9072 21.9167 15.7217L23.1708 14.2513C23.706 13.6233 24 12.8251 24 12C24 11.1749 23.706 10.3767 23.1708 9.74872L21.9167 8.27834C21.7587 8.09311 21.6632 7.86276 21.6437 7.62008L21.4898 5.69272C21.424 4.87061 21.0676 4.0988 20.4844 3.51561C19.9012 2.93243 19.1294 2.57599 18.3073 2.51016L16.3811 2.3563C16.138 2.33706 15.9072 2.24151 15.7217 2.08328L14.2513 0.829227C13.6233 0.293986 12.8251 0 12 0C11.1749 0 10.3767 0.293986 9.74872 0.829227L8.27834 2.08328C8.09311 2.24128 7.86276 2.33683 7.62008 2.3563L5.69272 2.51016C4.87061 2.57599 4.0988 2.93243 3.51561 3.51561C2.93243 4.0988 2.57599 4.87061 2.51016 5.69272L2.3563 7.61892C2.33706 7.86201 2.24151 8.0928 2.08328 8.27834L0.829227 9.74872C0.293986 10.3767 0 11.1749 0 12C0 12.8251 0.293986 13.6233 0.829227 14.2513L2.08328 15.7217C2.24128 15.9069 2.33683 16.1372 2.3563 16.3799L2.51016 18.3073C2.57599 19.1294 2.93243 19.9012 3.51561 20.4844C4.0988 21.0676 4.87061 21.424 5.69272 21.4898L7.61892 21.6437C7.86201 21.6629 8.0928 21.7585 8.27834 21.9167ZM7.7767 19.6499C8.44004 19.7024 9.06982 19.9632 9.57611 20.395L11.0461 21.6486C11.0459 21.6485 11.0462 21.6488 11.0461 21.6486C11.3121 21.8752 11.6506 22 12 22C12.3494 22 12.6874 21.8756 12.9534 21.6491L14.4237 20.3951C14.4236 20.3952 14.4237 20.3951 14.4237 20.3951C14.9291 19.964 15.5578 19.7032 16.2199 19.6501L18.1477 19.4962C18.496 19.4683 18.8231 19.3173 19.0702 19.0702C19.3173 18.8231 19.4683 18.496 19.4962 18.1477L19.6499 16.2233C19.7024 15.56 19.9632 14.9302 20.395 14.4239L21.6486 12.9539C21.8752 12.6879 22 12.3494 22 12C22 11.6506 21.8756 11.3126 21.6491 11.0466L7.7767 19.6499ZM21.6491 11.0466L20.3951 9.57632C20.3951 9.57627 20.3952 9.57637 20.3951 9.57632C19.9641 9.07091 19.7032 8.44223 19.6501 7.78006L19.4962 5.85234C19.4683 5.50398 19.3173 5.17694 19.0702 4.92983C18.8231 4.6828 18.4962 4.53178 18.148 4.50381C18.1479 4.5038 18.1481 4.50382 18.148 4.50381L16.2233 4.35007C16.2231 4.35005 16.2236 4.35009 16.2233 4.35007C16.2231 4.35005 16.2221 4.34997 16.2218 4.34995C15.559 4.29719 14.9298 4.0365 14.4239 3.60504L12.9539 2.35137C12.6879 2.12484 12.3494 2 12 2C11.6504 2 11.3122 2.12457 11.0461 2.35137L9.57632 3.60487C9.57627 3.60491 9.57637 3.60483 9.57632 3.60487C9.0709 4.03596 8.44222 4.29675 7.78006 4.34989L5.85234 4.50378C5.85219 4.5038 5.8525 4.50377 5.85234 4.50378C5.50416 4.53177 5.17684 4.68282 4.92983 4.92983C4.6828 5.17686 4.53178 5.50376 4.50381 5.85198C4.5038 5.8521 4.50382 5.85185 4.50381 5.85198L4.35007 7.7767C4.35005 7.77697 4.35009 7.77642 4.35007 7.7767C4.35005 7.77691 4.34997 7.77796 4.34995 7.77817C4.29719 8.44097 4.0365 9.07019 3.60504 9.57611L2.35137 11.0461C2.12457 11.3122 2 11.6504 2 12C2 12.3494 2.12442 12.6874 2.35095 12.9534L3.60487 14.4237C3.60483 14.4236 3.60491 14.4237 3.60487 14.4237C4.03596 14.9291 4.29675 15.5578 4.34989 16.2199L4.50378 18.1477C4.50377 18.1475 4.5038 18.1478 4.50378 18.1477C4.53177 18.4958 4.68282 18.8232 4.92983 19.0702C5.17694 19.3173 5.50398 19.4683 5.85234 19.4962L7.7767 19.6499",
                  fill: "white"
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M11.862 0.00274455C11.9079 0.000917917 11.9539 0 12 0C12.8251 0 13.6233 0.293986 14.2513 0.829227L15.7217 2.08328C15.9072 2.24151 16.138 2.33706 16.3811 2.3563L18.3073 2.51016C19.1294 2.57599 19.9012 2.93243 20.4844 3.51561C21.0676 4.0988 21.424 4.87061 21.4898 5.69272L21.6437 7.62008C21.6632 7.86276 21.7587 8.09311 21.9167 8.27834L23.1708 9.74872C23.525 10.1643 23.7735 10.6544 23.9011 11.1776C22.4327 12.3162 20.5512 13 18.5 13C17.1165 13 15.8102 12.6889 14.6557 12.137L16.2885 10.5042C16.4993 10.286 16.6159 9.99374 16.6132 9.69042C16.6106 9.38709 16.4889 9.09693 16.2744 8.88243C16.0599 8.66794 15.7698 8.54627 15.4665 8.54364C15.1631 8.541 14.8709 8.65761 14.6527 8.86834L12.6856 10.8355C11.0326 9.37599 10 7.30149 10 5C10 3.10937 10.6968 1.37192 11.862 0.00274455Z",
                  fill: "white",
                  fillOpacity: "0.4"
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  fillRule: "evenodd",
                  clipRule: "evenodd",
                  d: "M8.27834 21.9167L9.74872 23.1708C10.3767 23.706 11.1749 24 12 24C12.8251 24 13.6233 23.706 14.2513 23.1708L15.7217 21.9167C15.9069 21.7587 16.1372 21.6632 16.3799 21.6437L18.3073 21.4898C19.1294 21.424 19.9012 21.0676 20.4844 20.4844C21.0676 19.9012 21.424 19.1294 21.4898 18.3073L21.6437 16.3811C21.6629 16.138 21.7585 15.9072 21.9167 15.7217L23.1708 14.2513C23.706 13.6233 24 12.8251 24 12C24 11.1749 23.706 10.3767 23.1708 9.74872L21.9167 8.27834C21.7587 8.09311 21.6632 7.86276 21.6437 7.62008L21.4898 5.69272C21.424 4.87061 21.0676 4.0988 20.4844 3.51561C19.9012 2.93243 19.1294 2.57599 18.3073 2.51016L16.3811 2.3563C16.138 2.33706 15.9072 2.24151 15.7217 2.08328L14.2513 0.829227C13.6233 0.293986 12.8251 0 12 0C11.1749 0 10.3767 0.293986 9.74872 0.829227L8.27834 2.08328C8.09311 2.24128 7.86276 2.33683 7.62008 2.3563L5.69272 2.51016C4.87061 2.57599 4.0988 2.93243 3.51561 3.51561C2.93243 4.0988 2.57599 4.87061 2.51016 5.69272L2.3563 7.61892C2.33706 7.86201 2.24151 8.0928 2.08328 8.27834L0.829227 9.74872C0.293986 10.3767 0 11.1749 0 12C0 12.8251 0.293986 13.6233 0.829227 14.2513L2.08328 15.7217C2.24128 15.9069 2.33683 16.1372 2.3563 16.3799L2.51016 18.3073C2.57599 19.1294 2.93243 19.9012 3.51561 20.4844C4.0988 21.0676 4.87061 21.424 5.69272 21.4898L7.61892 21.6437C7.86201 21.6629 8.0928 21.7585 8.27834 21.9167ZM7.7767 19.6499C8.44004 19.7024 9.06982 19.9632 9.57611 20.395L11.0461 21.6486C11.0459 21.6485 11.0462 21.6488 11.0461 21.6486C11.3121 21.8752 11.6506 22 12 22C12.3494 22 12.6874 21.8756 12.9534 21.6491L14.4237 20.3951C14.4236 20.3952 14.4237 20.3951 14.4237 20.3951C14.9291 19.964 15.5578 19.7032 16.2199 19.6501L18.1477 19.4962C18.496 19.4683 18.8231 19.3173 19.0702 19.0702C19.3173 18.8231 19.4683 18.496 19.4962 18.1477L19.6499 16.2233C19.7024 15.56 19.9632 14.9302 20.395 14.4239L21.6486 12.9539C21.8752 12.6879 22 12.3494 22 12C22 11.6506 21.8756 11.3126 21.6491 11.0466L7.7767 19.6499ZM21.6491 11.0466L20.3951 9.57632C20.3951 9.57627 20.3952 9.57637 20.3951 9.57632C19.9641 9.07091 19.7032 8.44223 19.6501 7.78006L19.4962 5.85234C19.4683 5.50398 19.3173 5.17694 19.0702 4.92983C18.8231 4.6828 18.4962 4.53178 18.148 4.50381C18.1479 4.5038 18.1481 4.50382 18.148 4.50381L16.2233 4.35007C16.2231 4.35005 16.2236 4.35009 16.2233 4.35007C16.2231 4.35005 16.2221 4.34997 16.2218 4.34995C15.559 4.29719 14.9298 4.0365 14.4239 3.60504L12.9539 2.35137C12.6879 2.12484 12.3494 2 12 2C11.6504 2 11.3122 2.12457 11.0461 2.35137L9.57632 3.60487C9.57627 3.60491 9.57637 3.60483 9.57632 3.60487C9.0709 4.03596 8.44222 4.29675 7.78006 4.34989L5.85234 4.50378C5.85219 4.5038 5.8525 4.50377 5.85234 4.50378C5.50416 4.53177 5.17684 4.68282 4.92983 4.92983C4.6828 5.17686 4.53178 5.50376 4.50381 5.85198C4.5038 5.8521 4.50382 5.85185 4.50381 5.85198L4.35007 7.7767C4.35005 7.77697 4.35009 7.77642 4.35007 7.7767C4.35005 7.77691 4.34997 7.77796 4.34995 7.77817C4.29719 8.44097 4.0365 9.07019 3.60504 9.57611L2.35137 11.0461C2.12457 11.3122 2 11.6504 2 12C2 12.3494 2.12442 12.6874 2.35095 12.9534L3.60487 14.4237C3.60483 14.4236 3.60491 14.4237 3.60487 14.4237C4.03596 14.9291 4.29675 15.5578 4.34989 16.2199L4.50378 18.1477C4.50377 18.1475 4.5038 18.1478 4.50378 18.1477C4.53177 18.4958 4.68282 18.8232 4.92983 19.0702C5.17694 19.3173 5.50398 19.4683 5.85234 19.4962L7.7767 19.6499",
                  fill: "#3485FF"
                }
              )
            ] })
          }
        ),
        /* @__PURE__ */ jsx("span", { children: "Official Developer" })
      ]
    }
  );
};
var ChipOfficialIcon_default = ChipOfficialIcon;
var bgColorConfig = {
  primary: "!bg-primary text-white",
  secondary: "!bg-secondary text-accent",
  featured: "!bg-featured text-accent",
  premier: "!bg-premier text-accent",
  orange: "!bg-orange text-white",
  red: "!bg-red text-white",
  npl: "!bg-npl text-white",
  green: "!bg-keppel text-[#003730]"
};
var Chip = ({
  className: overriderClassName,
  iconPosition,
  icon,
  theme = "secondary",
  type = "default",
  label = "Lorem",
  onClick
}) => {
  const customClassName = `w-fit text-xs leading-4 font-normal py-1.5 px-2 rounded-full ${bgColorConfig[theme]} flex ${iconPosition === "right" ? "flex-row" : "flex-row-reverse"} items-center justify-center grow-0 shrink-0`;
  if (type === "official") return /* @__PURE__ */ jsx(ChipOfficialIcon_default, {});
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(customClassName, overriderClassName),
      onClick,
      children: [
        label,
        icon
      ]
    }
  );
};
var Chip_default = Chip;
var DEFAULT_IMAGE = "https://www.rumah123.com/asset-core/images/placeholder.png";
var sizes = {
  sm: "w-[24px] h-[24px]",
  md: "w-[42px] h-[42px]"
};
var Avatar = ({ src, size = "md", className, alt }) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "rounded-full border border-solid border-greyText relative overflow-hidden shrink-0",
        sizes[size]
      ),
      children: /* @__PURE__ */ jsx(
        "img",
        {
          alt,
          className: cn(
            "object-cover w-full h-full object-center",
            className
          ),
          src: src || DEFAULT_IMAGE,
          onError: ({ currentTarget }) => {
            const target = currentTarget;
            target.onerror = null;
            target.src = DEFAULT_IMAGE;
          }
        }
      )
    }
  );
};
var Avatar_default = Avatar;
var WidgetInternalLinkItem = ({
  id = "",
  category,
  href,
  title,
  caption,
  priceLabel,
  priceString,
  pricePeriodString,
  priceChangePercentage,
  titleUrl
}) => {
  return /* @__PURE__ */ jsxs(
    "a",
    {
      href,
      title: titleUrl || title,
      "data-test-id": `${id}-internal-link-item${category ? `-${category?.toLowerCase().replace(/\s+/g, "-")}` : ""}`,
      className: "w-full rounded-lg border border-solid border-[#f4f4f6] flex flex-col overflow-hidden cursor-pointer no-underline items-start justify-between foo",
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col px-2 pt-2 pb-1 items-start", children: [
          /* @__PURE__ */ jsx("div", { className: "self-stretch justify-center text-[#0d1a35] text-xs font-bold whitespace-pre text-ellipsis overflow-hidden mb-1", children: title }),
          caption && /* @__PURE__ */ jsx("div", { className: "self-stretch justify-center text-[#868d9a] text-[10px] font-normal", children: caption })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "min-h-5 self-stretch px-2 py-1 bg-[#f4f4f6] outline-1 outline-offset-[-1px] outline-[#f4f4f6] inline-flex justify-between items-center", children: [
          priceLabel ? /* @__PURE__ */ jsx("div", { className: "justify-start text-[#0d1a35] text-[10px] font-normal leading-none", children: priceLabel }) : /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-1", children: [
            priceString && /* @__PURE__ */ jsx("span", { className: "text-[#0d1a35] text-[10px] font-bold leading-none", children: priceString }),
            pricePeriodString && /* @__PURE__ */ jsx("span", { className: "text-[#0d1a35] text-[10px] font-normal leading-none", children: pricePeriodString })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex justify-center items-center", children: [
            priceLabel && (priceString || pricePeriodString) && /* @__PURE__ */ jsxs("div", { className: "flex items-center flex-wrap", children: [
              priceString && /* @__PURE__ */ jsx("span", { className: "text-[#0d1a35] text-[10px] font-bold leading-none whitespace-pre", children: priceString }),
              pricePeriodString && /* @__PURE__ */ jsx("span", { className: "text-[#0d1a35] text-[10px] font-normal leading-none", children: pricePeriodString })
            ] }),
            priceChangePercentage && priceChangePercentage !== 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsxs(
                "div",
                {
                  className: clsx5(
                    "justify-start text-[10px] font-bold leading-none ml-1",
                    priceChangePercentage > 0 ? "text-[#38baa7]" : "text-[#2951a3]"
                  ),
                  children: [
                    priceChangePercentage > 0 ? "+" : "",
                    Math.floor(priceChangePercentage * 10) / 10,
                    "%"
                  ]
                }
              ),
              /* @__PURE__ */ jsx("svg", { width: "12", height: "12", children: /* @__PURE__ */ jsx(
                "use",
                {
                  href: priceChangePercentage > 0 ? "#double-chevron-up" : "#double-chevron-down"
                }
              ) })
            ] })
          ] })
        ] })
      ]
    }
  );
};
var WidgetInternalLink = ({
  id,
  title: titleGroup,
  linkGroups,
  initialCount = 6
}) => {
  const [expanded, setExpanded] = useState(false);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("div", { className: "w-full p-4 rounded-lg border border-solid border-[#e7e8eb]", children: [
      titleGroup && /* @__PURE__ */ jsx(
        "div",
        {
          className: "text-[#0d1a35] text-sm font-bold leading-normal mb-4",
          "data-test-id": `${id}-internal-link-subtitle${titleGroup ? `-${titleGroup?.toLowerCase().replace(/\s+/g, "-")}` : ""}`,
          children: titleGroup
        }
      ),
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: clsx5(
            "relative flex flex-col w-full",
            expanded ? "pb-0" : "pb-4"
          ),
          children: [
            /* @__PURE__ */ jsx("div", { className: "w-full gap-3 grid grid-cols-2", children: linkGroups.slice(
              0,
              expanded ? linkGroups.length : initialCount
            ).map(
              ({
                title,
                titleUrl,
                caption,
                href,
                priceChangePercentage,
                priceString,
                pricePeriodString,
                priceLabel
              }, itemIndex) => /* @__PURE__ */ jsx(
                WidgetInternalLinkItem,
                {
                  ...{
                    id,
                    category: titleGroup,
                    title,
                    titleUrl,
                    caption,
                    href,
                    priceChangePercentage,
                    priceString,
                    pricePeriodString,
                    priceLabel
                  }
                },
                itemIndex
              )
            ) }),
            linkGroups.length > initialCount && /* @__PURE__ */ jsx(
              "div",
              {
                className: clsx5(
                  "flex w-full justify-center items-end bg-gradient-to-t from-white to-transparent",
                  expanded ? "relative" : "absolute left-0 right-0 bottom-0 h-10"
                ),
                children: /* @__PURE__ */ jsx(
                  "button",
                  {
                    onClick: () => setExpanded(!expanded),
                    className: "justify-center items-center text-accent border-0 cursor-pointer bg-white flex w-6 h-6 rounded-full shadow",
                    children: /* @__PURE__ */ jsx(
                      "svg",
                      {
                        width: "12",
                        height: "12",
                        className: expanded ? "rotate-180" : "rotate-0",
                        children: /* @__PURE__ */ jsx("use", { href: "#chevron-down" })
                      }
                    )
                  }
                )
              }
            )
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("svg", { width: "12", height: "12", className: "hidden", children: [
      /* @__PURE__ */ jsxs("symbol", { id: "double-chevron-up", viewBox: "0 0 12 12", children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M5.99939 5.33936C4.98884 6.18175 3.99422 7.01067 3 7.83756L3 9.51377C3.51058 9.09339 4.02115 8.67342 4.53051 8.25222C5.01864 7.84858 5.50473 7.44167 5.9998 7.02945C6.99891 7.86123 7.98864 8.68526 9 9.52724L9 7.8449C8.30658 7.25964 7.60867 6.68049 6.91157 6.09971C6.60751 5.84626 6.30304 5.59281 5.99939 5.33976L5.99939 5.33936Z",
            fill: "#38BAA7"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M6.91157 3.00995C6.60751 2.7565 6.30304 2.50304 5.99939 2.25C4.98884 3.09198 3.99422 3.92091 3 4.7482L3 6.42442C3.51058 6.00404 4.02115 5.58406 4.53051 5.16287C5.01864 4.75922 5.50473 4.35231 5.9998 3.94009C6.99891 4.77187 7.98864 5.5959 9 6.43789L9 4.75555C8.30658 4.17028 7.60867 3.59114 6.91157 3.01036L6.91157 3.00995Z",
            fill: "#38BAA7"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("symbol", { id: "double-chevron-down", viewBox: "0 0 12 12", children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M6.00061 6.66064C7.01116 5.81825 8.00578 4.98933 9 4.16244L9 2.48623C8.48942 2.90661 7.97885 3.32658 7.46949 3.74778C6.98136 4.15142 6.49527 4.55833 6.0002 4.97055C5.00109 4.13877 4.01136 3.31474 3 2.47276L3 4.1551C3.69342 4.74036 4.39133 5.31951 5.08843 5.90029C5.39249 6.15374 5.69696 6.40719 6.00061 6.66024L6.00061 6.66064Z",
            fill: "#2951A3"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M5.08843 8.99005C5.39249 9.2435 5.69696 9.49696 6.00061 9.75C7.01116 8.90802 8.00578 8.07909 9 7.2518L9 5.57558C8.48942 5.99596 7.97885 6.41594 7.46949 6.83713C6.98136 7.24078 6.49527 7.64769 6.0002 8.05991C5.00109 7.22813 4.01136 6.4041 3 5.56211L3 7.24445C3.69342 7.82972 4.39133 8.40886 5.08843 8.98964L5.08843 8.99005Z",
            fill: "#2951A3"
          }
        )
      ] }),
      /* @__PURE__ */ jsx("symbol", { id: "chevron-down", viewBox: "0 0 12 12", children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M1.84205 4.28991L5.90855 8.19366C6.05367 8.33316 6.28317 8.33316 6.4283 8.19366L10.4948 4.28991C10.6444 4.14628 10.6493 3.90853 10.5057 3.75928C10.362 3.61003 10.1243 3.60478 9.97505 3.74841L5.90855 7.65216H6.4283L2.3618 3.74841C2.21217 3.60478 1.9748 3.60966 1.83117 3.75928C1.68755 3.90891 1.69242 4.14628 1.84205 4.28991Z",
          fill: "#2951A3"
        }
      ) })
    ] })
  ] });
};
var WidgetInternalLinkAccordion = ({
  id = "",
  title,
  linkGroups,
  initialCount = 6,
  expandLabel = "Muat Lebih Banyak"
}) => {
  const [expanded, setExpanded] = useState([]);
  const [groupExpanded, setGroupExpanded] = useState([0]);
  const handleToggle = (index) => {
    if (groupExpanded.includes(index)) {
      setGroupExpanded(groupExpanded.filter((i) => i !== index));
    } else {
      setGroupExpanded([...groupExpanded, index]);
    }
  };
  const handleExpand = (index) => {
    if (expanded.includes(index)) {
      setExpanded(expanded.filter((i) => i !== index));
    } else {
      setExpanded([...expanded, index]);
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("div", { className: "w-full p-4 rounded-lg border border-solid border-[#e7e8eb]", children: [
      title && /* @__PURE__ */ jsx(
        "div",
        {
          className: "text-[#0d1a35] text-sm font-bold leading-normal mb-3",
          "data-test-id": `${id}-internal-link-title${title ? `-${title?.toLowerCase().replace(/\s+/g, "-")}` : ""}`,
          children: title
        }
      ),
      /* @__PURE__ */ jsx("div", { className: "flex flex-col w-full gap-6", children: linkGroups.map(
        ({
          title: titleGroup,
          icon,
          links,
          ctaAction,
          ctaLabel
        }, groupIndex) => /* @__PURE__ */ jsxs(
          "div",
          {
            className: "w-full inline-flex flex-col justify-start items-center",
            children: [
              titleGroup && /* @__PURE__ */ jsxs(
                "button",
                {
                  onClick: () => handleToggle(groupIndex),
                  className: "w-full py-2 bg-white border-0 border-b-[0.50px] border-[#cfd1d7] border-solid flex justify-between items-center gap-2 text-left",
                  "data-test-id": `${id}-internal-link-subtitle${titleGroup ? `-${titleGroup?.toLowerCase().replace(/\s+/g, "-")}` : ""}`,
                  children: [
                    icon?.(),
                    /* @__PURE__ */ jsx("div", { className: "justify-start text-[#0d1a35] text-xs font-bold leading-none flex-grow", children: titleGroup }),
                    /* @__PURE__ */ jsx(
                      "svg",
                      {
                        width: "12",
                        height: "12",
                        className: clsx5(
                          "transition-all duration-300 overflow-hidden",
                          groupExpanded.includes(
                            groupIndex
                          ) ? "rotate-180" : ""
                        ),
                        children: /* @__PURE__ */ jsx(
                          "path",
                          {
                            d: "M1.84205 4.28991L5.90855 8.19366C6.05367 8.33316 6.28317 8.33316 6.4283 8.19366L10.4948 4.28991C10.6444 4.14628 10.6493 3.90853 10.5057 3.75928C10.362 3.61003 10.1243 3.60478 9.97505 3.74841L5.90855 7.65216H6.4283L2.3618 3.74841C2.21217 3.60478 1.9748 3.60966 1.83117 3.75928C1.68755 3.90891 1.69242 4.14628 1.84205 4.28991Z",
                            fill: "#20282E"
                          }
                        )
                      }
                    )
                  ]
                }
              ),
              /* @__PURE__ */ jsxs(
                "div",
                {
                  className: clsx5(
                    "w-full transition-all duration-300 overflow-hidden",
                    groupExpanded.includes(groupIndex) ? "max-h-[2000px] mt-3" : "max-h-0"
                  ),
                  children: [
                    /* @__PURE__ */ jsx(
                      "div",
                      {
                        className: clsx5(
                          "w-full flex flex-col gap-3 md:grid md:grid-cols-2"
                        ),
                        children: links.slice(
                          0,
                          expanded.includes(groupIndex) ? links.length : initialCount
                        ).map(
                          ({
                            title: title2,
                            caption,
                            href,
                            priceChangePercentage,
                            priceString,
                            pricePeriodString,
                            priceLabel
                          }, itemIndex) => /* @__PURE__ */ jsx(
                            WidgetInternalLinkItem,
                            {
                              ...{
                                id,
                                category: titleGroup,
                                title: title2,
                                caption,
                                href,
                                priceChangePercentage,
                                priceString,
                                pricePeriodString,
                                priceLabel
                              }
                            },
                            itemIndex
                          )
                        )
                      }
                    ),
                    !expanded.includes(groupIndex) && links.length > initialCount && /* @__PURE__ */ jsx("div", { className: "flex w-full justify-center md:justify-start", children: /* @__PURE__ */ jsxs(
                      "button",
                      {
                        onClick: () => handleExpand(groupIndex),
                        className: "justify-start text-[#2951a3] text-xs font-bold leading-none border-0 mt-3 cursor-pointer bg-white flex gap-2",
                        children: [
                          /* @__PURE__ */ jsx("span", { children: expandLabel }),
                          /* @__PURE__ */ jsx("svg", { width: "12", height: "12", children: /* @__PURE__ */ jsx("use", { href: "#chevron-down" }) })
                        ]
                      }
                    ) }),
                    ctaAction && ctaLabel && /* @__PURE__ */ jsxs("div", { className: "flex w-full justify-center", children: [
                      typeof ctaAction === "function" && /* @__PURE__ */ jsxs(
                        "button",
                        {
                          onClick: ctaAction,
                          className: "px-4 py-2 bg-white rounded-lg border border-solid border-[#2951a3] inline-flex justify-center items-center gap-2 cursor-pointer mt-3",
                          children: [
                            /* @__PURE__ */ jsx("div", { className: "text-center justify-start text-[#2951a3] text-xs font-bold leading-none", children: ctaLabel }),
                            /* @__PURE__ */ jsx("svg", { width: "12", height: "12", children: /* @__PURE__ */ jsx("use", { href: "#arrow-down" }) })
                          ]
                        }
                      ),
                      typeof ctaAction === "string" && /* @__PURE__ */ jsxs(
                        "a",
                        {
                          href: ctaAction,
                          className: "px-4 py-2 bg-white rounded-lg border border-solid border-[#2951a3] inline-flex justify-center items-center gap-2 cursor-pointer mt-3",
                          children: [
                            /* @__PURE__ */ jsx("div", { className: "text-center justify-start text-[#2951a3] text-xs font-bold leading-none", children: ctaLabel }),
                            /* @__PURE__ */ jsx("svg", { width: "12", height: "12", children: /* @__PURE__ */ jsx("use", { href: "#arrow-down" }) })
                          ]
                        }
                      )
                    ] })
                  ]
                }
              )
            ]
          },
          groupIndex
        )
      ) })
    ] }),
    /* @__PURE__ */ jsxs("svg", { width: "12", height: "12", className: "hidden", children: [
      /* @__PURE__ */ jsxs("symbol", { id: "double-chevron-up", viewBox: "0 0 12 12", children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M5.99939 5.33936C4.98884 6.18175 3.99422 7.01067 3 7.83756L3 9.51377C3.51058 9.09339 4.02115 8.67342 4.53051 8.25222C5.01864 7.84858 5.50473 7.44167 5.9998 7.02945C6.99891 7.86123 7.98864 8.68526 9 9.52724L9 7.8449C8.30658 7.25964 7.60867 6.68049 6.91157 6.09971C6.60751 5.84626 6.30304 5.59281 5.99939 5.33976L5.99939 5.33936Z",
            fill: "#38BAA7"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M6.91157 3.00995C6.60751 2.7565 6.30304 2.50304 5.99939 2.25C4.98884 3.09198 3.99422 3.92091 3 4.7482L3 6.42442C3.51058 6.00404 4.02115 5.58406 4.53051 5.16287C5.01864 4.75922 5.50473 4.35231 5.9998 3.94009C6.99891 4.77187 7.98864 5.5959 9 6.43789L9 4.75555C8.30658 4.17028 7.60867 3.59114 6.91157 3.01036L6.91157 3.00995Z",
            fill: "#38BAA7"
          }
        )
      ] }),
      /* @__PURE__ */ jsx("symbol", { id: "arrow-down", viewBox: "0 0 12 12", children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M5.24242 -2.29154e-07L6.75758 -2.95383e-07L6.75758 9.09091L10.9242 4.92424L12 6L6 12L-2.62268e-07 6L1.07576 4.92424L5.24242 9.09091L5.24242 -2.29154e-07Z",
          fill: "#2951A3"
        }
      ) }),
      /* @__PURE__ */ jsxs("symbol", { id: "double-chevron-down", viewBox: "0 0 12 12", children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M6.00061 6.66064C7.01116 5.81825 8.00578 4.98933 9 4.16244L9 2.48623C8.48942 2.90661 7.97885 3.32658 7.46949 3.74778C6.98136 4.15142 6.49527 4.55833 6.0002 4.97055C5.00109 4.13877 4.01136 3.31474 3 2.47276L3 4.1551C3.69342 4.74036 4.39133 5.31951 5.08843 5.90029C5.39249 6.15374 5.69696 6.40719 6.00061 6.66024L6.00061 6.66064Z",
            fill: "#2951A3"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M5.08843 8.99005C5.39249 9.2435 5.69696 9.49696 6.00061 9.75C7.01116 8.90802 8.00578 8.07909 9 7.2518L9 5.57558C8.48942 5.99596 7.97885 6.41594 7.46949 6.83713C6.98136 7.24078 6.49527 7.64769 6.0002 8.05991C5.00109 7.22813 4.01136 6.4041 3 5.56211L3 7.24445C3.69342 7.82972 4.39133 8.40886 5.08843 8.98964L5.08843 8.99005Z",
            fill: "#2951A3"
          }
        )
      ] }),
      /* @__PURE__ */ jsx("symbol", { id: "chevron-down", viewBox: "0 0 12 12", children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M1.84205 4.28991L5.90855 8.19366C6.05367 8.33316 6.28317 8.33316 6.4283 8.19366L10.4948 4.28991C10.6444 4.14628 10.6493 3.90853 10.5057 3.75928C10.362 3.61003 10.1243 3.60478 9.97505 3.74841L5.90855 7.65216H6.4283L2.3618 3.74841C2.21217 3.60478 1.9748 3.60966 1.83117 3.75928C1.68755 3.90891 1.69242 4.14628 1.84205 4.28991Z",
          fill: "#2951A3"
        }
      ) })
    ] })
  ] });
};
var WidgetInternalLinkBasic = ({
  id = "",
  title: titleGroup,
  links
}) => {
  return /* @__PURE__ */ jsxs("div", { className: "w-full p-4 rounded-lg border border-solid border-[#e7e8eb] widget-internal-link-basic", children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "text-[#0d1a35] text-sm font-bold leading-normal mb-3",
        "data-test-id": `${id}-internal-link-title${titleGroup ? `-${titleGroup?.toLowerCase().replace(/\s+/g, "-")}` : ""}`,
        children: titleGroup
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "grid grid-cols-2 gap-3", children: links.map(({ text, href }) => /* @__PURE__ */ jsx(
      "a",
      {
        href,
        "data-test-id": `${id}-internal-link-item${titleGroup ? `-${titleGroup?.toLowerCase().replace(/\s+/g, "-")}` : ""}`,
        className: "w-full cursor-pointer no-underline text-xs leading-none text-[#0d1a35] hover:text-[#2951a3]",
        children: text
      },
      text
    )) })
  ] });
};

// src/components/ReactPortal.tsx
var ReactPortal = ReactPortalComponent;
var ModalRoomTypeFilter = ({
  open,
  onClose,
  onChange,
  onSubmit,
  defaultValue = [],
  textMap = {}
}) => {
  const [value, setValue] = useState(defaultValue);
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = () => {
    onSubmit(value);
  };
  const handleChange = (input) => {
    setIsLoading(true);
    const newValue = value.includes(input) ? value.filter((item) => item !== input) : [...value, input];
    setValue(newValue);
    onChange(newValue).then(() => setIsLoading(false));
  };
  const handleReset = () => {
    setIsLoading(true);
    setValue([]);
    onChange([]).then(() => setIsLoading(false));
  };
  const options = [
    { key: "studio", label: textMap["studio"] || "Studio" },
    { key: "room1", label: textMap["room1"] || "1 Kamar" },
    { key: "room2", label: textMap["room2"] || "2 Kamar" },
    { key: "room3", label: textMap["room3"] || "3 Kamar" },
    { key: "room4", label: textMap["room4"] || "4 Kamar" },
    { key: "room5plus", label: textMap["room5plus"] || "+ 5 Kamar" }
  ];
  return /* @__PURE__ */ jsx(ReactPortal, { children: /* @__PURE__ */ jsxs(
    DialogWithHeader,
    {
      preventCloseOnBackground: true,
      className: "md:w-[520px]",
      title: textMap["title"] || "Tipe Kamar",
      open,
      onClose,
      children: [
        /* @__PURE__ */ jsx("div", { className: "pb-6 flex gap-2 flex-wrap", children: options.map((option) => /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: cn(
              "px-2 py-1  rounded-2xl inline-flex justify-center items-center gap-2",
              value.includes(option.key) && "bg-primary text-white",
              !value.includes(option.key) && "bg-white border border-solid border-[#cfd1d7] text-[#868d9a]"
            ),
            onClick: () => handleChange(option.key),
            children: /* @__PURE__ */ jsx("span", { className: "text-center justify-start  text-sm font-normal leading-normal", children: option.label })
          },
          option.key
        )) }),
        /* @__PURE__ */ jsxs("div", { className: "flex justify-end gap-6 shadow-[0px_-2px_4px_0px_rgba(0,0,0,0.04)] pt-4", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              disabled: isLoading,
              onClick: handleReset,
              className: "bg-white flex justify-center cursor-pointer rounded-lg py-3 px-4 font-bold text-center text-primary border-none disabled:bg-disabledGreyLight disabled:text-[#738492]",
              children: textMap["resetText"] || "Reset Filter"
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              disabled: isLoading,
              onClick: handleSubmit,
              className: "bg-primary flex justify-center cursor-pointer rounded-lg py-3 px-4 font-bold text-center text-white border-none min-w-[150px]",
              children: isLoading ? /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(
                "div",
                {
                  className: "animate-spin inline-block w-3 h-3 border-0.5 border-white border-solid border-t-transparent rounded-full",
                  role: "status",
                  "aria-label": "loading",
                  children: /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Loading..." })
                }
              ) }) : textMap["submitText"] || "Tampilkan"
            }
          )
        ] })
      ]
    }
  ) });
};
var MortgageDialogConsent = ({
  title = "Tanya KPR",
  consentText = "Saya mau dapat penawaran menarik KPR dari Rumah123",
  onClose,
  onSubmit,
  propertyData,
  isLoading,
  ...rest
}) => {
  const inputRef = useRef(null);
  return /* @__PURE__ */ jsx(ReactPortal, { children: /* @__PURE__ */ jsxs(
    Dialog,
    {
      ...rest,
      style: {
        background: "linear-gradient(180deg, #C1D6FF -9.76%, #FFF 100%), linear-gradient(175deg, #E8EAFF 0.62%, #FAFAFF 91.63%), #FFF"
      },
      className: "md:w-[536px]",
      onClose,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsx("p", { className: "text-2xl text-accent font-bold", children: title }),
          /* @__PURE__ */ jsx(
            "button",
            {
              className: "outline-none border-none flex cursor-pointer bg-transparent ml-auto mb-6",
              onClick: (e) => {
                e.stopPropagation();
                if (onClose) {
                  onClose();
                }
              },
              children: /* @__PURE__ */ jsx(
                "svg",
                {
                  width: "16",
                  height: "16",
                  viewBox: "0 0 16 16",
                  fill: "none",
                  xmlns: "http://www.w3.org/2000/svg",
                  children: /* @__PURE__ */ jsx(
                    "path",
                    {
                      fillRule: "evenodd",
                      clipRule: "evenodd",
                      d: "M9.88546 8.00017L15.6093 2.27629C16.1307 1.75496 16.1307 0.912314 15.6093 0.390992C15.088 -0.130331 14.2454 -0.130331 13.724 0.390992L8.00017 6.11487L2.27629 0.390992C1.75496 -0.130331 0.912314 -0.130331 0.390992 0.390992C-0.130331 0.912314 -0.130331 1.75496 0.390992 2.27629L6.11487 8.00017L0.390992 13.724C-0.130331 14.2454 -0.130331 15.088 0.390992 15.6093C0.650986 15.8693 0.992313 16 1.33364 16C1.67497 16 2.01629 15.8693 2.27629 15.6093L8.00017 9.88546L13.724 15.6093C13.984 15.8693 14.3254 16 14.6667 16C15.008 16 15.3493 15.8693 15.6093 15.6093C16.1307 15.088 16.1307 14.2454 15.6093 13.724L9.88546 8.00017Z",
                      fill: "#979797"
                    }
                  )
                }
              )
            }
          )
        ] }),
        /* @__PURE__ */ jsx("hr", { className: "mt-4 mb-6 border-0 border-solid border-b border-neutral30" }),
        /* @__PURE__ */ jsxs("div", { className: "flex gap-x-3", children: [
          /* @__PURE__ */ jsx("div", { className: "w-24 h-24 aspect-square shrink-0", children: /* @__PURE__ */ jsx(
            "img",
            {
              className: "w-full h-full rounded-md object-cover",
              src: propertyData.imgSrc,
              alt: propertyData.description
            }
          ) }),
          /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
            /* @__PURE__ */ jsx("div", { className: "px-2 py-1.5 mb-1 rounded-full bg-[#ECEDEE] font-medium w-max text-[#20282E] text-3xs", children: propertyData.label }),
            /* @__PURE__ */ jsx("div", { className: "text-sm text-primary font-bold", children: propertyData.priceLabel }),
            /* @__PURE__ */ jsx("div", { className: "text-sm text-accent whitespace-pre text-ellipsis overflow-hidden min-w-0", children: propertyData.description }),
            /* @__PURE__ */ jsx("div", { className: "text-xs text-[#868D9A]", children: propertyData.location }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-x-2 text-accent font-medium text-xs", children: [
              Boolean(propertyData.bathrooms) && /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-x-1", children: [
                /* @__PURE__ */ jsx(
                  "svg",
                  {
                    width: "12",
                    height: "12",
                    viewBox: "0 0 12 12",
                    fill: "none",
                    xmlns: "http://www.w3.org/2000/svg",
                    children: /* @__PURE__ */ jsx(
                      "path",
                      {
                        d: "M7.18414 9.81034H4.87752C3.12514 9.81034 1.80664 8.62984 1.80664 7.00121L1.43164 7.37621H10.6296L10.2546 7.00121C10.2546 8.62984 8.93614 9.81034 7.18377 9.81034H7.18414ZM7.18414 10.56C9.33327 10.56 11.0046 9.06334 11.0046 7.00121C11.0046 6.79421 10.8366 6.62621 10.6296 6.62621H1.43164C1.22464 6.62621 1.05664 6.79421 1.05664 7.00121C1.05664 9.06371 2.72839 10.56 4.87714 10.56H7.18377H7.18414ZM6.43714 2.08571C6.64977 1.69684 7.07577 1.52246 7.78677 1.52246H7.88914C8.74227 1.52246 9.43002 2.15471 9.43002 2.90846V7.00121C9.43002 7.20821 9.59802 7.37621 9.80502 7.37621C10.012 7.37621 10.18 7.20821 10.18 7.00121V2.90846C10.18 1.72534 9.14314 0.772461 7.88952 0.772461H7.78714C6.83052 0.772461 6.14802 1.05184 5.77977 1.72609C5.68039 1.90796 5.74714 2.13559 5.92902 2.23496C6.11089 2.33434 6.33852 2.26759 6.43789 2.08571H6.43714ZM4.68852 3.66296C4.68852 2.93921 5.27539 2.35234 5.99914 2.35234C6.72289 2.35234 7.30977 2.93921 7.30977 3.66296L7.68477 3.28796H4.31352L4.68852 3.66296ZM3.93852 3.66296C3.93852 3.86996 4.10652 4.03796 4.31352 4.03796H7.68477C7.89177 4.03796 8.05977 3.86996 8.05977 3.66296C8.05977 2.52484 7.13727 1.60234 5.99914 1.60234C4.86102 1.60234 3.93852 2.52521 3.93852 3.66296ZM8.36277 10.2011L8.97252 11.217C9.07902 11.3947 9.30927 11.4521 9.48701 11.3456C9.66477 11.2391 9.72214 11.0088 9.61564 10.8311L9.00589 9.81521C8.89939 9.63746 8.66914 9.58009 8.49139 9.68659C8.31364 9.79309 8.25627 10.0233 8.36277 10.2011ZM2.87914 9.81521L2.26939 10.8311C2.16289 11.0088 2.22027 11.2391 2.39802 11.3456C2.57577 11.4521 2.80602 11.3947 2.91252 11.217L3.52227 10.2011C3.62877 10.0233 3.57139 9.79309 3.39364 9.68659C3.21589 9.58009 2.98564 9.63746 2.87914 9.81521Z",
                        fill: "#868D9A"
                      }
                    )
                  }
                ),
                propertyData.bathrooms
              ] }),
              Boolean(propertyData.bedrooms) && /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-x-1", children: [
                /* @__PURE__ */ jsx(
                  "svg",
                  {
                    width: "12",
                    height: "11",
                    viewBox: "0 0 12 11",
                    fill: "none",
                    xmlns: "http://www.w3.org/2000/svg",
                    children: /* @__PURE__ */ jsx(
                      "path",
                      {
                        d: "M1.49692 9.12056V10.0329C1.49692 10.2268 1.65405 10.3839 1.84792 10.3839C2.0418 10.3839 2.19892 10.2268 2.19892 10.0329V9.12056C2.19892 8.92668 2.0418 8.76956 1.84792 8.76956C1.65405 8.76956 1.49692 8.92668 1.49692 9.12056ZM9.91755 9.12056V10.0329C9.91755 10.2268 10.0747 10.3839 10.2685 10.3839C10.4624 10.3839 10.6195 10.2268 10.6195 10.0329V9.12056C10.6195 8.92668 10.4624 8.76956 10.2685 8.76956C10.0747 8.76956 9.91755 8.92668 9.91755 9.12056ZM10.6195 7.01531V8.45156C10.6195 8.62893 10.4812 8.77068 10.318 8.77068H1.79842C1.63305 8.77068 1.49692 8.63118 1.49692 8.45156V7.01531C1.49692 6.82143 1.3398 6.66431 1.14592 6.66431C0.952047 6.66431 0.794922 6.82143 0.794922 7.01531V8.45156C0.794922 9.01481 1.24117 9.47231 1.79805 9.47231H10.3177C10.8723 9.47231 11.3208 9.01256 11.3208 8.45156V7.01531C11.3208 6.82143 11.1637 6.66431 10.9698 6.66431C10.7759 6.66431 10.6188 6.82143 10.6188 7.01531H10.6195ZM9.19867 2.27831C9.24142 2.39493 9.21517 2.43243 9.08842 2.43243H3.02767C2.90092 2.43243 2.87467 2.39456 2.91742 2.27831L3.07905 1.83881C3.22042 1.45443 3.69892 1.12106 4.10917 1.12106H8.0073C8.41755 1.12106 8.89642 1.45518 9.03742 1.83881L9.19905 2.27831H9.19867ZM9.85755 2.03643L9.69592 1.59693C9.4533 0.937309 8.71192 0.419434 8.0073 0.419434H4.10917C3.4053 0.419434 2.66355 0.936184 2.42055 1.59693L2.25892 2.03643C2.04742 2.61206 2.41455 3.13443 3.02805 3.13443H9.0888C9.70267 3.13443 10.0694 2.61206 9.85792 2.03643H9.85755ZM9.15855 3.12693L10.6027 6.57918C10.6368 6.66093 10.6342 6.66468 10.5265 6.66468H1.58992C1.48342 6.66468 1.4793 6.65906 1.51305 6.57843L2.95042 3.12618C3.02505 2.94731 2.9403 2.74181 2.76142 2.66756C2.58255 2.59331 2.37705 2.67768 2.3028 2.85656L0.865422 6.30881C0.636297 6.85931 0.991422 7.36631 1.5903 7.36631H10.5269C11.128 7.36631 11.4809 6.85931 11.2503 6.30806L9.80617 2.85581C9.73155 2.67693 9.52567 2.59293 9.34717 2.66756C9.16867 2.74218 9.0843 2.94806 9.15892 3.12656L9.15855 3.12693Z",
                        fill: "#868D9A"
                      }
                    )
                  }
                ),
                propertyData.bedrooms
              ] }),
              Boolean(propertyData.landSize) && /* @__PURE__ */ jsxs("span", { className: "flex", children: [
                /* @__PURE__ */ jsxs("span", { className: cn("text-greyText"), children: [
                  propertyData.buildingSizeLabel || "LT",
                  ":\xA0"
                ] }),
                " ",
                propertyData.landSize
              ] }),
              Boolean(propertyData.buildingSize) && /* @__PURE__ */ jsxs("span", { className: "flex", children: [
                /* @__PURE__ */ jsxs("span", { className: cn("text-greyText"), children: [
                  propertyData.buildingSizeLabel || "LB",
                  ":\xA0"
                ] }),
                " ",
                propertyData.buildingSize
              ] })
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsxs("label", { className: "flex items-center cursor-pointer gap-x-2 mb-4 mt-6", children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "checkbox",
              className: "peer hidden",
              ref: inputRef,
              defaultChecked: true
            }
          ),
          /* @__PURE__ */ jsx("div", { className: "h-4 w-4 peer-checked:bg-primary transition-[background-color] rounded-md bg-white peer-checked:[&>svg]:opacity-100", children: /* @__PURE__ */ jsx(
            "svg",
            {
              width: "16",
              height: "16",
              viewBox: "0 0 16 16",
              fill: "none",
              xmlns: "http://www.w3.org/2000/svg",
              className: "opacity-0 transition-[opacity]",
              children: /* @__PURE__ */ jsx(
                "path",
                {
                  fillRule: "evenodd",
                  clipRule: "evenodd",
                  d: "M13.3661 4.23431C13.6785 4.54673 13.6785 5.05327 13.3661 5.36569L6.96608 11.7657C6.65366 12.0781 6.14712 12.0781 5.83471 11.7657L2.63471 8.56569C2.32229 8.25327 2.32229 7.74673 2.63471 7.43431C2.94712 7.1219 3.45366 7.1219 3.76608 7.43431L6.40039 10.0686L12.2347 4.23431C12.5471 3.9219 13.0537 3.9219 13.3661 4.23431Z",
                  fill: "white"
                }
              )
            }
          ) }),
          /* @__PURE__ */ jsx("span", { className: "text-accent select-none text-3xs", children: consentText })
        ] }),
        /* @__PURE__ */ jsxs(
          "button",
          {
            className: "w-full justify-center flex items-center gap-x-2 disabled:opacity-70 disabled:cursor-default transition-opacity text-base text-center px-4 py-2 rounded-lg font-bold border-transparent bg-green cursor-pointer text-white min-h-10",
            onClick: () => {
              onSubmit({ isConsentChecked: inputRef.current?.checked || false });
            },
            disabled: isLoading,
            children: [
              isLoading ? /* @__PURE__ */ jsx(loader_default, { className: "mx-0" }) : /* @__PURE__ */ jsx(
                "svg",
                {
                  width: "17",
                  height: "16",
                  viewBox: "0 0 17 16",
                  fill: "none",
                  xmlns: "http://www.w3.org/2000/svg",
                  children: /* @__PURE__ */ jsx(
                    "path",
                    {
                      d: "M13.4035 3.0345C12.094 1.722 10.3505 1 8.497 1C4.672 1 1.5595 4.1125 1.5595 7.9375C1.5595 9.1595 1.878 10.353 2.4845 11.4065L1.5 15.0005L5.178 14.035C6.1905 14.588 7.331 14.879 8.4935 14.879H8.4965C12.3185 14.879 15.4995 11.7665 15.4995 7.9415C15.4995 6.0885 14.712 4.3475 13.4025 3.035L13.4035 3.0345ZM8.4975 13.7095C7.46 13.7095 6.4445 13.4315 5.56 12.9065L5.3505 12.7815L3.1695 13.3535L3.7505 11.2255L3.613 11.0065C3.035 10.088 2.7315 9.0285 2.7315 7.938C2.7315 4.76 5.319 2.1725 8.5005 2.1725C10.041 2.1725 11.488 2.7725 12.5755 3.863C13.663 4.9535 14.3315 6.4005 14.3285 7.941C14.3285 11.122 11.6755 13.7095 8.4975 13.7095ZM11.66 9.3905C11.488 9.303 10.635 8.8845 10.4755 8.828C10.316 8.7685 10.2005 8.7405 10.085 8.9155C9.9695 9.0905 9.638 9.478 9.535 9.597C9.435 9.7125 9.332 9.728 9.16 9.6405C8.1415 9.131 7.4725 8.731 6.8005 7.578C6.6225 7.2715 6.9785 7.2935 7.31 6.631C7.3665 6.5155 7.338 6.4155 7.2945 6.328C7.251 6.2405 6.904 5.3875 6.76 5.0405C6.6195 4.703 6.4755 4.75 6.3695 4.7435C6.2695 4.7375 6.154 4.7375 6.0385 4.7375C5.923 4.7375 5.7355 4.7815 5.576 4.953C5.4165 5.128 4.9695 5.547 4.9695 6.4C4.9695 7.253 5.5915 8.078 5.6755 8.1935C5.763 8.309 6.8975 10.059 8.638 10.8125C9.738 11.2875 10.1695 11.328 10.719 11.247C11.0535 11.197 11.744 10.8285 11.8875 10.422C12.031 10.0155 12.0315 9.669 11.9875 9.597C11.947 9.519 11.831 9.475 11.6595 9.3905H11.66Z",
                      fill: "white"
                    }
                  )
                }
              ),
              "Kontak Agen"
            ]
          }
        )
      ]
    }
  ) });
};
var DEFAULT_IMAGE2 = "https://www.rumah123.com/asset-core/images/placeholder.png";
var PostEnquirySuggestionCard = ({
  address,
  attribute,
  checkboxValue,
  id,
  media,
  onCardImpressed,
  onChangeCheckbox,
  priceTag,
  propertyType,
  title,
  channel,
  lang,
  isCardMobile
}) => {
  const { observe } = useInView({
    unobserveOnEnter: true,
    onEnter: () => {
      if (onCardImpressed) {
        onCardImpressed(id);
      }
    }
  });
  const generateFacility = () => {
    const facilities = `${attribute.kostFacilitiesRoom}, ${attribute.hotelFacilitiesRoom}`.split(", ").filter((facility) => facility.length > 0);
    return /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center relative overflow-hidden", children: [
      /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-x-1 text-sm shrink-0", children: [
        /* @__PURE__ */ jsxs(
          "svg",
          {
            className: "text-greyText translate-y-[-2px]",
            width: "16",
            height: "16",
            viewBox: "0 0 16 16",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            children: [
              /* @__PURE__ */ jsxs("g", { "clip-path": "url(#clip0_3268_17968)", children: [
                /* @__PURE__ */ jsx(
                  "path",
                  {
                    d: "M15.7241 13.8928C15.7973 13.8928 15.8675 13.8637 15.9192 13.812C15.9709 13.7603 16 13.6901 16 13.6169V3.7019C16 3.64498 15.9824 3.58945 15.9496 3.54293C15.9168 3.4964 15.8704 3.46116 15.8168 3.44204L8.09268 0.683417C8.03272 0.662168 7.96727 0.662168 7.9073 0.683417L0.183172 3.44204C0.129562 3.46116 0.0831793 3.4964 0.0503855 3.54293C0.0175918 3.58945 -7.85295e-06 3.64498 2.6286e-09 3.7019V13.6329C-7.85295e-06 13.6898 0.0175918 13.7454 0.0503855 13.7919C0.0831793 13.8384 0.129562 13.8737 0.183172 13.8928L7.9073 16.6514C7.96725 16.6728 8.03274 16.6728 8.09268 16.6514L15.8168 13.8928C15.8704 13.8737 15.9169 13.8384 15.9497 13.7919C15.9825 13.7454 16.0001 13.6899 16.0001 13.6329C16.0001 13.576 15.9825 13.5205 15.9497 13.4739C15.9169 13.4274 15.8704 13.3922 15.8168 13.3731L8.27586 10.6812V1.04673C8.27586 0.973563 8.24679 0.903397 8.19506 0.851663C8.14332 0.799929 8.07316 0.770865 7.99999 0.770865C7.92683 0.770865 7.85666 0.799929 7.80493 0.851663C7.7532 0.903397 7.72413 0.973563 7.72413 1.04673V10.6812L0.551724 13.2415V3.89638L7.99999 1.23624L15.4483 3.89638V13.6169C15.4483 13.6901 15.4773 13.7603 15.5291 13.812C15.5808 13.8637 15.651 13.8928 15.7241 13.8928ZM7.99999 16.0986L4.95834 15.0122L7.99999 13.9259L11.0416 15.0122L7.99999 16.0986ZM11.8621 14.7193L8.82041 13.6329L11.8621 12.5466L14.9037 13.6329L11.8621 14.7193ZM7.99999 11.1673L11.0416 12.2536L7.99999 13.34L4.95834 12.2536L7.99999 11.1673ZM4.13793 12.5466L7.17958 13.6329L4.13793 14.7193L1.096 13.6329L4.13793 12.5466Z",
                    fill: "#868D9A"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "path",
                  {
                    d: "M1.65524 13.1893C1.58207 13.1893 1.51191 13.1602 1.46017 13.1085C1.40844 13.0568 1.37938 12.9866 1.37938 12.9134L1.33789 6.78369C1.33788 6.72577 1.35609 6.66931 1.38996 6.62232C1.42383 6.57534 1.47163 6.5402 1.52658 6.5219L4.00934 5.69431C4.05081 5.6805 4.09497 5.67674 4.13818 5.68334C4.18139 5.68994 4.22241 5.70672 4.25786 5.73229C4.29332 5.75785 4.32219 5.79148 4.3421 5.83039C4.362 5.86931 4.37238 5.91239 4.37237 5.9561L4.41385 10.2874V12.0859C4.41385 12.159 4.38479 12.2292 4.33306 12.2809C4.28132 12.3327 4.21116 12.3617 4.13799 12.3617C4.06483 12.3617 3.99466 12.3327 3.94293 12.2809C3.89119 12.2292 3.86213 12.159 3.86213 12.0859L3.82065 6.33872L1.88961 6.98259L1.9311 12.9134C1.9311 12.9866 1.90204 13.0568 1.8503 13.1085C1.79857 13.1602 1.7284 13.1893 1.65524 13.1893Z",
                    fill: "#868D9A"
                  }
                )
              ] }),
              /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx("clipPath", { id: "clip0_3268_17968", children: /* @__PURE__ */ jsx(
                "rect",
                {
                  width: "16",
                  height: "16",
                  fill: "white",
                  transform: "translate(0 0.66748)"
                }
              ) }) })
            ]
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "text-sm ml-1", children: attribute.roomSize })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "flex shrink-0 items-center flex-wrap", children: facilities.map((facility) => {
        return /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("div", { className: "w-[4px] h-[4px] bg-[#D9D9D9] rounded-full mx-2 text-white", children: "." }),
          /* @__PURE__ */ jsx("span", { className: "text-sm leading-6", children: facility })
        ] });
      }) }),
      /* @__PURE__ */ jsx("div", { className: "absolute bg-[linear-gradient(90deg,_rgba(255,_255,_255,_0)_0%,_#FFFFFF_50%)] w-10 h-full right-0" })
    ] });
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: observe,
      className: cn(
        "relative flex items-start bg-white rounded-md cursor-pointer",
        isCardMobile ? "flex-row gap-3" : "flex-row md:flex-col gap-3 md:gap-2"
      ),
      onClick: () => {
        onChangeCheckbox(!checkboxValue);
      },
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: cn(
              "flex-none rounded-lg overflow-hidden",
              isCardMobile ? "w-[96px] h-[96px] md:w-[96px] md:h-[96px] aspect-square" : "w-[96px] h-[96px] md:w-full md:h-auto aspect-square md:aspect-[3/2]"
            ),
            children: [
              /* @__PURE__ */ jsx(
                "img",
                {
                  className: "w-full h-full object-cover object-center",
                  alt: media[0] ? media[0].alt : "",
                  src: media[0] ? media[0].src : DEFAULT_IMAGE2,
                  loading: "lazy",
                  decoding: "async",
                  draggable: "false",
                  onError: ({ currentTarget }) => {
                    const target = currentTarget;
                    target.onerror = null;
                    target.src = DEFAULT_IMAGE2;
                  }
                }
              ),
              checkboxValue && onChangeCheckbox && /* @__PURE__ */ jsx("div", { className: "absolute top-[10px] left-[10px] bg-white rounded-lg", children: /* @__PURE__ */ jsx(
                Checkbox_default,
                {
                  name: `selected-properti-${id}`,
                  onChange: onChangeCheckbox,
                  value: checkboxValue,
                  children: /* @__PURE__ */ jsx(
                    CheckboxItem_default,
                    {
                      id: `selected-properti-${id}`,
                      value: 1,
                      className: "top-0 w-5 h-5",
                      size: 20
                    }
                  )
                }
              ) })
            ]
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "flex-auto flex flex-col items-start", children: [
          /* @__PURE__ */ jsx(
            Chip_default,
            {
              label: propertyType,
              className: "text-3xs leading-4 py-0.5 mb-1"
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "font-bold text-primary",
                isCardMobile ? "text-sm" : "text-sm md:text-lg"
              ),
              children: priceTag
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "font-normal line-clamp-1",
                isCardMobile ? "text-sm" : "text-sm md:text-base"
              ),
              children: title
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "font-normal text-slate-600",
                isCardMobile ? "text-xs" : "text-xs md:text-sm"
              ),
              children: address
            }
          ),
          channel?.toLowerCase() === "rent" && ["kost", "hotel", "boarding house"].includes(
            propertyType.toLowerCase()
          ) ? generateFacility() : /* @__PURE__ */ jsxs(
            ScrollableWrapper_default,
            {
              className: cn(
                "flex text-accent font-medium text-xs",
                isCardMobile ? "flex-row gap-2 items-center" : "flex-row md:flex-col items-center md:items-start gap-2 md:gap-1"
              ),
              children: [
                (Boolean(attribute?.bedroom) || Boolean(attribute?.bathroom) || Boolean(attribute?.carport)) && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-x-2", children: [
                  Boolean(attribute?.bedroom) && /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-x-1", children: [
                    /* @__PURE__ */ jsx(
                      "svg",
                      {
                        className: "text-greyText",
                        width: "16",
                        height: "14",
                        viewBox: "0 0 16 14",
                        fill: "none",
                        xmlns: "http://www.w3.org/2000/svg",
                        children: /* @__PURE__ */ jsx(
                          "path",
                          {
                            d: "M2.26562 11.9375C2.26562 11.875 2.28646 11.8229 2.32812 11.7812C2.38021 11.7292 2.4375 11.7031 2.5 11.7031C2.57292 11.7031 2.63021 11.7292 2.67188 11.7812C2.72396 11.8229 2.75 11.875 2.75 11.9375V12.9062C2.75 12.9688 2.72396 13.0208 2.67188 13.0625C2.63021 13.1146 2.57292 13.1406 2.5 13.1406C2.4375 13.1406 2.38021 13.1146 2.32812 13.0625C2.28646 13.0208 2.26562 12.9688 2.26562 12.9062V11.9375ZM13.3438 11.9375C13.3438 11.875 13.3646 11.8229 13.4062 11.7812C13.4583 11.7292 13.5156 11.7031 13.5781 11.7031C13.651 11.7031 13.7083 11.7292 13.75 11.7812C13.8021 11.8229 13.8281 11.875 13.8281 11.9375V12.9062C13.8281 12.9688 13.8021 13.0208 13.75 13.0625C13.7083 13.1146 13.651 13.1406 13.5781 13.1406C13.5156 13.1406 13.4583 13.1146 13.4062 13.0625C13.3646 13.0208 13.3438 12.9688 13.3438 12.9062V11.9375ZM14.5312 7.84375H15.0156V10.7344C15.0156 11.0677 14.8958 11.3542 14.6562 11.5938C14.4271 11.8229 14.1458 11.9375 13.8125 11.9375H2.23438C1.91146 11.9375 1.63021 11.8229 1.39062 11.5938C1.16146 11.3542 1.04688 11.0677 1.04688 10.7344V7.84375H1.53125V10.7344C1.53125 10.9323 1.59896 11.1042 1.73438 11.25C1.86979 11.3958 2.03646 11.4688 2.23438 11.4688H13.8125C14.0104 11.4688 14.1771 11.3958 14.3125 11.25C14.4583 11.1042 14.5312 10.9323 14.5312 10.7344V7.84375ZM3.89062 2.79688V3.26562C3.67188 3.26562 3.51562 3.16146 3.42188 2.95312C3.33854 2.74479 3.31771 2.50521 3.35938 2.23438L3.45312 1.71875C3.47396 1.63542 3.48438 1.55208 3.48438 1.46875C3.48438 1.375 3.47917 1.26042 3.46875 1.125C3.45833 0.989583 3.45312 0.895833 3.45312 0.84375C3.45312 0.791667 3.45833 0.739583 3.46875 0.6875C3.5 0.583333 3.55208 0.505208 3.625 0.453125C3.69792 0.390625 3.78646 0.359375 3.89062 0.359375H7.34375C7.45833 0.359375 7.55208 0.390625 7.625 0.453125C7.69792 0.505208 7.74479 0.583333 7.76562 0.6875C7.77604 0.739583 7.78125 0.791667 7.78125 0.84375C7.78125 0.895833 7.77604 0.989583 7.76562 1.125C7.75521 1.26042 7.75 1.375 7.75 1.46875C7.76042 1.55208 7.77083 1.63542 7.78125 1.71875L7.875 2.23438C7.92708 2.50521 7.90625 2.74479 7.8125 2.95312C7.71875 3.16146 7.5625 3.26562 7.34375 3.26562V2.79688C7.36458 2.79688 7.38542 2.75521 7.40625 2.67188C7.42708 2.57812 7.42708 2.45833 7.40625 2.3125L7.3125 1.8125C7.29167 1.69792 7.27604 1.58854 7.26562 1.48438C7.26562 1.38021 7.27083 1.25 7.28125 1.09375C7.29167 0.989583 7.29688 0.921875 7.29688 0.890625C7.29688 0.848958 7.29688 0.817708 7.29688 0.796875C7.29688 0.796875 7.30208 0.807292 7.3125 0.828125C7.33333 0.838542 7.34375 0.84375 7.34375 0.84375H3.89062C3.90104 0.84375 3.91146 0.838542 3.92188 0.828125C3.93229 0.807292 3.9375 0.796875 3.9375 0.796875C3.9375 0.817708 3.9375 0.848958 3.9375 0.890625C3.9375 0.921875 3.94271 0.989583 3.95312 1.09375C3.96354 1.25 3.96354 1.38021 3.95312 1.48438C3.95312 1.58854 3.94271 1.69792 3.92188 1.8125L3.84375 2.3125C3.8125 2.45833 3.80729 2.57812 3.82812 2.67188C3.84896 2.75521 3.86979 2.79688 3.89062 2.79688ZM8.70312 2.79688V3.26562C8.49479 3.26562 8.34375 3.16146 8.25 2.95312C8.15625 2.74479 8.13542 2.50521 8.1875 2.23438L8.26562 1.71875C8.28646 1.63542 8.29688 1.55208 8.29688 1.46875C8.29688 1.375 8.29167 1.26042 8.28125 1.125C8.27083 0.989583 8.26562 0.895833 8.26562 0.84375C8.27604 0.791667 8.28646 0.739583 8.29688 0.6875C8.31771 0.583333 8.36458 0.505208 8.4375 0.453125C8.51042 0.390625 8.59896 0.359375 8.70312 0.359375H12.1562C12.2708 0.359375 12.3646 0.390625 12.4375 0.453125C12.5104 0.505208 12.5573 0.583333 12.5781 0.6875C12.5885 0.739583 12.5938 0.791667 12.5938 0.84375C12.5938 0.895833 12.5885 0.989583 12.5781 1.125C12.5781 1.26042 12.5781 1.375 12.5781 1.46875C12.5781 1.55208 12.5833 1.63542 12.5938 1.71875L12.6875 2.23438C12.7396 2.50521 12.7188 2.74479 12.625 2.95312C12.5312 3.16146 12.375 3.26562 12.1562 3.26562V2.79688C12.1771 2.79688 12.1979 2.75521 12.2188 2.67188C12.2396 2.57812 12.2396 2.45833 12.2188 2.3125L12.125 1.8125C12.1042 1.69792 12.0938 1.58854 12.0938 1.48438C12.0938 1.38021 12.099 1.25 12.1094 1.09375C12.1094 0.989583 12.1094 0.921875 12.1094 0.890625C12.1094 0.848958 12.1094 0.817708 12.1094 0.796875C12.1094 0.796875 12.1146 0.807292 12.125 0.828125C12.1458 0.838542 12.1562 0.84375 12.1562 0.84375H8.70312C8.71354 0.84375 8.72396 0.838542 8.73438 0.828125C8.75521 0.807292 8.76562 0.796875 8.76562 0.796875C8.75521 0.817708 8.75 0.848958 8.75 0.890625C8.75 0.921875 8.75521 0.989583 8.76562 1.09375C8.77604 1.25 8.78125 1.38021 8.78125 1.48438C8.78125 1.58854 8.77083 1.69792 8.75 1.8125L8.65625 2.3125C8.625 2.45833 8.61979 2.57812 8.64062 2.67188C8.67188 2.75521 8.69271 2.79688 8.70312 2.79688ZM14.4531 7.5625L12.5781 3.5625C12.5469 3.48958 12.4792 3.42188 12.375 3.35938C12.2812 3.29688 12.1927 3.26562 12.1094 3.26562H3.92188C3.83854 3.26562 3.75 3.29688 3.65625 3.35938C3.5625 3.42188 3.5 3.48958 3.46875 3.5625L1.59375 7.5625C1.52083 7.71875 1.51562 7.84896 1.57812 7.95312C1.64062 8.04688 1.76042 8.09375 1.9375 8.09375H14.1094C14.2969 8.09375 14.4167 8.04688 14.4688 7.95312C14.5312 7.84896 14.526 7.71875 14.4531 7.5625ZM14.8906 7.35938C15.0365 7.67188 15.0312 7.95312 14.875 8.20312C14.7292 8.45312 14.474 8.57812 14.1094 8.57812H1.9375C1.58333 8.57812 1.32812 8.45312 1.17188 8.20312C1.01562 7.95312 1.01562 7.67188 1.17188 7.35938L3.03125 3.35938C3.10417 3.20312 3.22396 3.07292 3.39062 2.96875C3.56771 2.85417 3.74479 2.79688 3.92188 2.79688H12.1094C12.2865 2.79688 12.4635 2.85417 12.6406 2.96875C12.8177 3.07292 12.9427 3.20312 13.0156 3.35938L14.8906 7.35938Z",
                            fill: "currentColor"
                          }
                        )
                      }
                    ),
                    attribute?.bedroom
                  ] }),
                  Boolean(attribute?.bathroom) && /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-x-1", children: [
                    /* @__PURE__ */ jsx(
                      "svg",
                      {
                        className: "text-greyText",
                        width: "14",
                        height: "15",
                        viewBox: "0 0 14 15",
                        fill: "none",
                        xmlns: "http://www.w3.org/2000/svg",
                        children: /* @__PURE__ */ jsx(
                          "path",
                          {
                            d: "M8.5625 12.7031C9.19792 12.7031 9.79167 12.599 10.3438 12.3906C10.8854 12.1823 11.3542 11.8958 11.75 11.5312C12.1562 11.1667 12.474 10.7344 12.7031 10.2344C12.9323 9.72396 13.0469 9.17188 13.0469 8.57812L13.2812 8.8125H0.6875L0.921875 8.57812C0.921875 9.17188 1.03646 9.72396 1.26562 10.2344C1.49479 10.7344 1.80729 11.1667 2.20312 11.5312C2.60938 11.8958 3.08854 12.1823 3.64062 12.3906C4.18229 12.599 4.77083 12.7031 5.40625 12.7031H8.5625ZM8.5625 13.1875H5.40625C4.70833 13.1875 4.05729 13.0729 3.45312 12.8438C2.84896 12.6146 2.32292 12.2969 1.875 11.8906C1.42708 11.474 1.07812 10.9844 0.828125 10.4219C0.567708 9.85938 0.4375 9.24479 0.4375 8.57812C0.4375 8.51562 0.458333 8.45833 0.5 8.40625C0.552083 8.35417 0.614583 8.32812 0.6875 8.32812H13.2812C13.3542 8.32812 13.4115 8.35417 13.4531 8.40625C13.5052 8.45833 13.5312 8.51562 13.5312 8.57812C13.5312 9.24479 13.401 9.85938 13.1406 10.4219C12.8906 10.9844 12.5417 11.474 12.0938 11.8906C11.6458 12.2969 11.1198 12.6146 10.5156 12.8438C9.91146 13.0729 9.26042 13.1875 8.5625 13.1875ZM7.90625 2.39062C7.875 2.44271 7.82812 2.47917 7.76562 2.5C7.70312 2.52083 7.64062 2.52083 7.57812 2.5C7.51562 2.46875 7.47396 2.42188 7.45312 2.35938C7.43229 2.29688 7.4375 2.23438 7.46875 2.17188C7.70833 1.69271 8.05729 1.30729 8.51562 1.01562C8.98438 0.723958 9.48958 0.578125 10.0312 0.578125H10.1719C10.9635 0.578125 11.6406 0.859375 12.2031 1.42188C12.7656 1.98438 13.0469 2.66146 13.0469 3.45312V8.57812C13.0469 8.64062 13.0208 8.69792 12.9688 8.75C12.9271 8.79167 12.8698 8.8125 12.7969 8.8125C12.7344 8.8125 12.6771 8.79167 12.625 8.75C12.5833 8.69792 12.5625 8.64062 12.5625 8.57812V3.45312C12.5625 2.79688 12.3281 2.23438 11.8594 1.76562C11.3906 1.29688 10.8281 1.0625 10.1719 1.0625H10.0312C9.58333 1.0625 9.16667 1.18229 8.78125 1.42188C8.39583 1.66146 8.10417 1.98438 7.90625 2.39062ZM5.78125 4.45312L5.53125 4.21875H9.89062L9.65625 4.45312C9.65625 3.92188 9.46354 3.46875 9.07812 3.09375C8.70312 2.70833 8.25 2.51562 7.71875 2.51562C7.17708 2.51562 6.71875 2.70833 6.34375 3.09375C5.96875 3.46875 5.78125 3.92188 5.78125 4.45312ZM5.29688 4.45312C5.29688 3.78646 5.53125 3.21875 6 2.75C6.46875 2.27083 7.04167 2.03125 7.71875 2.03125C8.38542 2.03125 8.95312 2.27083 9.42188 2.75C9.90104 3.21875 10.1406 3.78646 10.1406 4.45312C10.1406 4.52604 10.1146 4.58854 10.0625 4.64062C10.0208 4.68229 9.96354 4.70312 9.89062 4.70312H5.53125C5.46875 4.70312 5.41146 4.68229 5.35938 4.64062C5.31771 4.58854 5.29688 4.52604 5.29688 4.45312ZM10.4062 12.8281C10.375 12.7656 10.3646 12.7031 10.375 12.6406C10.3958 12.5781 10.4375 12.526 10.5 12.4844C10.5521 12.4531 10.6094 12.4479 10.6719 12.4688C10.7448 12.4792 10.7969 12.5156 10.8281 12.5781L11.5625 13.7812C11.5938 13.8438 11.599 13.9062 11.5781 13.9688C11.5677 14.0312 11.5312 14.0781 11.4688 14.1094C11.4167 14.151 11.3542 14.1615 11.2812 14.1406C11.2188 14.1302 11.1719 14.0938 11.1406 14.0312L10.4062 12.8281ZM2.90625 12.5781C2.9375 12.5156 2.98438 12.4792 3.04688 12.4688C3.10938 12.4479 3.17188 12.4531 3.23438 12.4844C3.28646 12.526 3.32292 12.5781 3.34375 12.6406C3.36458 12.7031 3.35417 12.7656 3.3125 12.8281L2.59375 14.0312C2.55208 14.0938 2.5 14.1302 2.4375 14.1406C2.375 14.1615 2.3125 14.151 2.25 14.1094C2.19792 14.0781 2.16146 14.0312 2.14062 13.9688C2.13021 13.9062 2.14062 13.8438 2.17188 13.7812L2.90625 12.5781Z",
                            fill: "currentColor"
                          }
                        )
                      }
                    ),
                    attribute?.bathroom
                  ] }),
                  Boolean(attribute?.carport) && /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-x-1", children: [
                    /* @__PURE__ */ jsx(
                      "svg",
                      {
                        className: "text-greyText",
                        width: "15",
                        height: "15",
                        viewBox: "0 0 15 15",
                        fill: "none",
                        xmlns: "http://www.w3.org/2000/svg",
                        children: /* @__PURE__ */ jsx(
                          "path",
                          {
                            xmlns: "http://www.w3.org/2000/svg",
                            d: "M2.75 8.20312C2.53125 8.21354 2.34375 8.30208 2.1875 8.46875C2.04167 8.625 1.96875 8.81771 1.96875 9.04688C1.97917 9.26562 2.0625 9.45312 2.21875 9.60938C2.38542 9.76562 2.57812 9.83854 2.79688 9.82812C3.02604 9.81771 3.21354 9.73438 3.35938 9.57812C3.51562 9.41146 3.58854 9.21875 3.57812 9C3.57812 8.77083 3.49479 8.58333 3.32812 8.4375C3.17188 8.28125 2.97917 8.20312 2.75 8.20312ZM2.78125 7.71875C3.13542 7.71875 3.4375 7.84896 3.6875 8.10938C3.9375 8.35938 4.0625 8.66146 4.0625 9.01562C4.0625 9.36979 3.9375 9.67708 3.6875 9.9375C3.4375 10.1875 3.13542 10.3125 2.78125 10.3125C2.42708 10.3125 2.11979 10.1875 1.85938 9.9375C1.60938 9.67708 1.48438 9.36979 1.48438 9.01562C1.48438 8.66146 1.60938 8.35938 1.85938 8.10938C2.11979 7.84896 2.42708 7.71875 2.78125 7.71875ZM11.2969 8.20312C11.0781 8.20312 10.8906 8.28646 10.7344 8.45312C10.5781 8.60938 10.5 8.79688 10.5 9.01562C10.5 9.24479 10.5781 9.4375 10.7344 9.59375C10.8906 9.75 11.0781 9.82812 11.2969 9.82812C11.526 9.82812 11.7188 9.75 11.875 9.59375C12.0312 9.4375 12.1094 9.24479 12.1094 9.01562C12.1094 8.79688 12.0312 8.60938 11.875 8.45312C11.7188 8.28646 11.526 8.20312 11.2969 8.20312ZM11.2969 7.71875C11.651 7.71875 11.9531 7.84896 12.2031 8.10938C12.4635 8.35938 12.5938 8.66146 12.5938 9.01562C12.5938 9.36979 12.4635 9.67708 12.2031 9.9375C11.9531 10.1875 11.651 10.3125 11.2969 10.3125C10.9427 10.3125 10.6406 10.1875 10.3906 9.9375C10.1406 9.67708 10.0156 9.36979 10.0156 9.01562C10.0156 8.66146 10.1406 8.35938 10.3906 8.10938C10.6406 7.84896 10.9427 7.71875 11.2969 7.71875ZM5.34375 9.34375C5.28125 9.34375 5.22396 9.32292 5.17188 9.28125C5.13021 9.22917 5.10938 9.17188 5.10938 9.10938C5.10938 9.03646 5.13021 8.97917 5.17188 8.9375C5.22396 8.88542 5.28125 8.85938 5.34375 8.85938H8.73438C8.79688 8.85938 8.84896 8.88542 8.89062 8.9375C8.94271 8.97917 8.96875 9.03646 8.96875 9.10938C8.96875 9.17188 8.94271 9.22917 8.89062 9.28125C8.84896 9.32292 8.79688 9.34375 8.73438 9.34375H5.34375ZM13.0312 6.09375C13.3333 6.20833 13.5729 6.40104 13.75 6.67188C13.9375 6.93229 14.0312 7.22396 14.0312 7.54688V10.8281C14.0312 10.974 14.0052 11.1146 13.9531 11.25C13.901 11.375 13.8229 11.4844 13.7188 11.5781C13.6146 11.6823 13.4948 11.7604 13.3594 11.8125C13.224 11.8646 13.0885 11.8906 12.9531 11.8906H12.7969V13.1094C12.7969 13.401 12.6875 13.651 12.4688 13.8594C12.2604 14.0677 12.0104 14.1719 11.7188 14.1719H10.875C10.5729 14.1719 10.3125 14.0677 10.0938 13.8594C9.88542 13.651 9.78125 13.401 9.78125 13.1094V11.8906H4.20312V13.1094C4.20312 13.401 4.09375 13.651 3.875 13.8594C3.66667 14.0677 3.41667 14.1719 3.125 14.1719H2.28125C1.97917 14.1719 1.71875 14.0677 1.5 13.8594C1.29167 13.651 1.1875 13.401 1.1875 13.1094V11.8906H1.125C0.989583 11.8906 0.854167 11.8646 0.71875 11.8125C0.583333 11.7604 0.463542 11.6823 0.359375 11.5781C0.255208 11.4844 0.177083 11.375 0.125 11.25C0.0729167 11.1146 0.046875 10.974 0.046875 10.8281V7.54688C0.046875 7.22396 0.135417 6.93229 0.3125 6.67188C0.5 6.40104 0.744792 6.20833 1.04688 6.09375L1.73438 2.95312C1.86979 2.30729 2.1875 1.76562 2.6875 1.32812C3.1875 0.880208 3.89583 0.65625 4.8125 0.65625H9.3125C10.2083 0.65625 10.901 0.875 11.3906 1.3125C11.8802 1.75 12.1875 2.29167 12.3125 2.9375L13.0312 6.09375ZM2.21875 3.04688L1.5625 6.01562C1.58333 6.02604 1.61458 6.03646 1.65625 6.04688C1.69792 6.04688 1.74479 6.05729 1.79688 6.07812C1.93229 6.09896 2.07812 6.125 2.23438 6.15625C2.40104 6.1875 2.58333 6.21354 2.78125 6.23438C3.34375 6.31771 3.97917 6.38021 4.6875 6.42188C5.39583 6.46354 6.18229 6.48438 7.04688 6.48438C7.91146 6.48438 8.69792 6.46354 9.40625 6.42188C10.125 6.38021 10.7604 6.31771 11.3125 6.23438C11.5104 6.21354 11.6875 6.1875 11.8438 6.15625C12.0104 6.125 12.1615 6.09896 12.2969 6.07812C12.349 6.06771 12.3906 6.05729 12.4219 6.04688C12.4635 6.03646 12.4948 6.02604 12.5156 6.01562L11.8438 3.04688C11.7292 2.49479 11.4688 2.04167 11.0625 1.6875C10.6667 1.32292 10.0833 1.14062 9.3125 1.14062H4.8125C4.02083 1.14062 3.41667 1.32292 3 1.6875C2.58333 2.05208 2.32292 2.50521 2.21875 3.04688ZM3.71875 13.1094V11.8906H1.67188V13.1094C1.67188 13.2656 1.72917 13.401 1.84375 13.5156C1.95833 13.6302 2.10417 13.6875 2.28125 13.6875H3.125C3.29167 13.6875 3.43229 13.6302 3.54688 13.5156C3.66146 13.401 3.71875 13.2656 3.71875 13.1094ZM12.3125 13.1094V11.8906H10.2656V13.1094C10.2656 13.2656 10.3229 13.401 10.4375 13.5156C10.5625 13.6302 10.7083 13.6875 10.875 13.6875H11.7188C11.8854 13.6875 12.026 13.6302 12.1406 13.5156C12.2552 13.401 12.3125 13.2656 12.3125 13.1094ZM13.5469 10.8281V7.54688C13.5469 7.30729 13.474 7.09375 13.3281 6.90625C13.1823 6.70833 12.9948 6.57812 12.7656 6.51562L12.6562 6.48438C12.625 6.49479 12.5885 6.50521 12.5469 6.51562C12.5052 6.52604 12.4531 6.53646 12.3906 6.54688C12.2552 6.57812 12.1042 6.60938 11.9375 6.64062C11.7708 6.66146 11.5885 6.6875 11.3906 6.71875C10.8177 6.79167 10.1667 6.85417 9.4375 6.90625C8.71875 6.94792 7.92188 6.96875 7.04688 6.96875C6.17188 6.96875 5.375 6.94792 4.65625 6.90625C3.9375 6.85417 3.29167 6.79167 2.71875 6.71875C2.51042 6.6875 2.32292 6.66146 2.15625 6.64062C1.98958 6.60938 1.83854 6.57812 1.70312 6.54688C1.64062 6.52604 1.58333 6.51042 1.53125 6.5C1.48958 6.48958 1.45312 6.48438 1.42188 6.48438L1.3125 6.51562C1.08333 6.57812 0.895833 6.70833 0.75 6.90625C0.604167 7.09375 0.53125 7.30729 0.53125 7.54688V10.8281C0.53125 10.901 0.541667 10.974 0.5625 11.0469C0.59375 11.1198 0.640625 11.1823 0.703125 11.2344C0.755208 11.2969 0.817708 11.3438 0.890625 11.375C0.963542 11.3958 1.04167 11.4062 1.125 11.4062H12.9531C13.0365 11.4062 13.1146 11.3958 13.1875 11.375C13.2604 11.3438 13.3229 11.2969 13.375 11.2344C13.4375 11.1823 13.4792 11.1198 13.5 11.0469C13.5312 10.974 13.5469 10.901 13.5469 10.8281Z",
                            fill: "currentColor"
                          }
                        )
                      }
                    ),
                    attribute?.carport
                  ] })
                ] }),
                /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-x-2", children: [
                  Boolean(attribute?.landSize) && /* @__PURE__ */ jsxs("span", { className: "flex", children: [
                    /* @__PURE__ */ jsxs("span", { className: "text-greyText", children: [
                      lang === "en" ? "LA" : "LT",
                      ":\xA0"
                    ] }),
                    " ",
                    attribute?.landSize
                  ] }),
                  Boolean(attribute?.buildingSize) && /* @__PURE__ */ jsxs("span", { className: "flex", children: [
                    /* @__PURE__ */ jsxs("span", { className: "text-greyText", children: [
                      lang === "en" ? "BA" : "LB",
                      ":\xA0"
                    ] }),
                    " ",
                    attribute?.buildingSize
                  ] })
                ] })
              ]
            }
          )
        ] })
      ]
    }
  );
};
var PostEnquirySuggestion = ({
  isBulkEnquiry,
  items,
  mortgageInterest,
  onCardImpressed,
  onChangeCheckboxField,
  onChangeCheckboxSelectAll,
  onChangeMortgageCheckbox,
  onMortgageSectionImpressed,
  showMortgageSuggestion,
  valueSelectAll,
  lang
}) => {
  const { observe } = useInView({
    unobserveOnEnter: true,
    onEnter: () => {
      if (onMortgageSectionImpressed) onMortgageSectionImpressed();
    }
  });
  const handleChecboxSelectAll = (res) => {
    if (onChangeCheckboxSelectAll) {
      onChangeCheckboxSelectAll(res);
    }
  };
  const scrollRef = useRef(null);
  const [atBottom, setAtBottom] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
      checkScroll(el);
    }
  }, []);
  const checkScroll = (el) => {
    const isAtBottom = el.scrollHeight - el.scrollTop === el.clientHeight && el.scrollHeight > 340;
    setAtBottom(isAtBottom);
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    isBulkEnquiry && /* @__PURE__ */ jsxs("div", { className: "flex w-full justify-end gap-4 pt-1 pr-2 pl-0 md:pt-2 md:pr-6", children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs font-bold text-accent", children: lang === "en" ? "Select All" : "Pilih Semua" }),
      /* @__PURE__ */ jsx(
        Checkbox_default,
        {
          name: "selected-all",
          onChange: handleChecboxSelectAll,
          value: valueSelectAll || [],
          children: /* @__PURE__ */ jsx(
            CheckboxItem_default,
            {
              id: "selected-all",
              value: 1
            }
          )
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "relative mt-4 md:mt-0 max-h-[360px] md:max-h-[526px]"
        ),
        children: [
          atBottom && /* @__PURE__ */ jsx("div", { className: "absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-white to-transparent z-10 pointer-events-none" }),
          !atBottom && /* @__PURE__ */ jsx("div", { className: "absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent z-10 pointer-events-none" }),
          /* @__PURE__ */ jsx(
            "div",
            {
              ref: scrollRef,
              className: cn(
                "overflow-y-auto no-scrollbar flex flex-col max-h-[360px] md:max-h-[526px]"
              ),
              onScroll: (e) => checkScroll(e.currentTarget),
              children: /* @__PURE__ */ jsx("div", { className: "flex flex-col md:flex-row items-start gap-3 md:flex-nowrap pb-4 md:pb-6 md:border-0 md:border-b md:border-greyBorder md:border-solid", children: items.map(({ ...item }, index) => /* @__PURE__ */ jsx("div", { className: "relative flex-1", children: /* @__PURE__ */ jsx(
                PostEnquirySuggestionCard,
                {
                  ...item,
                  checkboxValue: item.checkboxValue,
                  onChangeCheckbox: (res) => {
                    onChangeCheckboxField(index, res);
                  },
                  onCardImpressed: (originId) => {
                    if (onCardImpressed)
                      onCardImpressed(originId);
                  },
                  isCardMobile: items?.length < 3,
                  lang
                }
              ) }, item.id)) })
            }
          )
        ]
      }
    ),
    showMortgageSuggestion && /* @__PURE__ */ jsxs(
      "div",
      {
        ref: observe,
        className: "mt-4 md:mt-6 sticky bottom-0 md:relative md:bottom-auto w-full flex items-center gap-4 rounded-lg px-4 md:pl-[120px] bg-gradient-to-b from-[#5A83D5] to-[#81BAE8]",
        children: [
          /* @__PURE__ */ jsx(
            "img",
            {
              className: "flex-none w-[88px] h-[81px] object-scale-down md:absolute md:left-4 md:bottom-0",
              alt: "mortgage icon",
              title: "mortgage icon",
              src: "https://www.rumah123.com/asset-core/images/assets/mortgage/mortgage-banner-asset.png",
              draggable: "false",
              decoding: "async",
              loading: "lazy"
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "w-full flex-auto leading-4 text-white py-3", children: [
            /* @__PURE__ */ jsx("div", { className: "text-xs font-bold mb-1", children: lang === "en" ? "I'm interested in mortgage" : "Saya tertarik untuk KPR" }),
            /* @__PURE__ */ jsx("p", { className: "text-3xs", children: lang === "en" ? "I want to know the interesting KPR property offers on Rumah123" : "Saya mau tahu penawaran menarik dengan beli properti lewat KPR di Rumah123" })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "flex-none flex items-center bg-white rounded-md", children: /* @__PURE__ */ jsx(
            Checkbox_default,
            {
              name: "mortgage",
              onChange: onChangeMortgageCheckbox,
              value: mortgageInterest,
              children: /* @__PURE__ */ jsx(
                CheckboxItem_default,
                {
                  id: "mortgage-interest",
                  value: 1,
                  className: "top-0 w-5 h-5",
                  size: 20
                }
              )
            }
          ) })
        ]
      }
    )
  ] });
};
var DEFAULT_IMAGE3 = "https://www.rumah123.com/asset-core/images/placeholder.png";
var PostEnquiryBulkCard = ({
  item,
  isVerified,
  hasPhone,
  hasWhatsapp,
  onClickPhone,
  onClickWhatsapp
}) => {
  const getAgentPhone = (item2) => {
    const agentPhone = item2.phoneNumbers && item2.phoneNumbers.length > 0 && isVerified ? item2.phoneNumbers[0] : "";
    return agentPhone;
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col bg-white border border-solid border-greyDarker rounded-lg shadow-sm p-4 gap-4", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2", children: [
      /* @__PURE__ */ jsx("div", { className: "relative w-24 h-24 flex-none rounded-lg overflow-hidden", children: /* @__PURE__ */ jsx(
        "img",
        {
          className: "w-full h-full object-cover object-center",
          src: item.media[0].src || DEFAULT_IMAGE3,
          alt: item.media[0].alt,
          title: item.media[0].alt,
          loading: "lazy",
          decoding: "async",
          draggable: "false",
          onError: ({ currentTarget }) => {
            const target = currentTarget;
            target.onerror = null;
            target.src = DEFAULT_IMAGE3;
          }
        }
      ) }),
      /* @__PURE__ */ jsxs("div", { className: "flex-auto flex flex-col gap-1", children: [
        /* @__PURE__ */ jsx(
          Chip_default,
          {
            label: item.propertyType,
            className: "text-3xs leading-4 py-0.5"
          }
        ),
        /* @__PURE__ */ jsx("div", { className: "text-sm font-bold text-primary", children: item.priceTag }),
        /* @__PURE__ */ jsx("div", { className: "text-xs font-normal line-clamp-1", children: item.title }),
        /* @__PURE__ */ jsx("div", { className: "text-xs font-normal text-slate-600", children: item.address })
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "w-full flex items-center justify-between border-0 border-t border-solid border-t-slate-300 pt-4", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-[10px]", children: [
        /* @__PURE__ */ jsx(
          Avatar_default,
          {
            src: item.agent?.agentImageUrl || DEFAULT_IMAGE3,
            alt: item.agent?.agentName || "",
            size: "sm"
          }
        ),
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("div", { className: "text-sm font-normal mb-0.5 line-clamp-1", children: item.agent?.agentName }),
          /* @__PURE__ */ jsx("div", { className: "text-xs text-slate-400 font-normal line-clamp-1", children: getAgentPhone(item) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        hasPhone && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "border border-solid border-primary hover:bg-primary active:bg-primaryPressed outline-none focus:ring-primaryFocused focus-visible:ring focus:ring-2 h-12 text-base leading-5 font-light flex-row-reverse items-center justify-center gap-1 w-12 rounded-lg px-4 transition-100 cursor-pointer text-primary bg-white hover:text-white active:text-white max-w-full inline-flex py-3",
            title: "+62878244...",
            name: "phone",
            onClick: (e) => {
              e.stopPropagation();
              onClickPhone(item);
            },
            children: /* @__PURE__ */ jsx("span", { className: "flex-none flex", children: /* @__PURE__ */ jsx(
              "svg",
              {
                xmlns: "http://www.w3.org/2000/svg",
                height: "20px",
                width: "20px",
                viewBox: "0 0 16 16",
                className: "flex-none",
                children: /* @__PURE__ */ jsx(
                  "path",
                  {
                    fillRule: "evenodd",
                    clipRule: "evenodd",
                    fill: "currentColor",
                    d: "M7.00328 5.88918C7.60455 5.28788 7.60455 4.31258 7.00339 3.71087L5.74358 2.45099C5.14233 1.84971 4.16717 1.84971 3.56509 2.45077L2.87458 3.14213C2.15709 3.85966 2.04494 4.98031 2.60478 5.82737C4.63816 8.90131 7.34916 11.6125 10.4228 13.6459C11.2687 14.205 12.3923 14.0925 13.1079 13.3758L13.7987 12.685C14.4004 12.0838 14.4004 11.1083 13.7987 10.5066L12.5394 9.2472C11.9381 8.64588 10.9624 8.64588 10.3611 9.2472L9.94122 9.66713L10.4145 9.64371C9.05027 8.52567 7.7226 7.19793 6.60728 5.83592L6.5839 6.30898L7.0034 5.88906L7.00328 5.88918ZM6.50405 5.39017L6.08456 5.81009C5.95641 5.93836 5.94631 6.14287 6.06118 6.28315C7.20609 7.68129 8.56682 9.0421 9.96707 10.1897C10.1074 10.3047 10.312 10.2946 10.4403 10.1663L10.8602 9.74634C11.1859 9.42068 11.7147 9.42068 12.0403 9.74634L13.2996 11.0057C13.6256 11.3317 13.6256 11.8601 13.2997 12.1857L12.6087 12.8769C12.1302 13.356 11.3783 13.4313 10.8121 13.0571C7.81773 11.0761 5.1745 8.43271 3.19354 5.43803C2.81858 4.87069 2.89356 4.12142 3.37383 3.64112L4.06413 2.94998C4.39022 2.62443 4.9188 2.62443 5.24447 2.95012L6.50417 4.20989C6.82983 4.53585 6.82983 5.06437 6.50417 5.39005L6.50405 5.39017Z"
                  }
                )
              }
            ) })
          }
        ),
        hasWhatsapp && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "bg-green hover:bg-lightGreen active:bg-green outline-none focus:ring-lightGreen focus-visible:ring focus:ring-2 text-white h-12 text-base leading-5 font-light flex-row-reverse items-center justify-center flex gap-1 w-12 rounded-lg px-4 transition-100 cursor-pointer max-w-full py-3",
            title: "WhatsApp",
            name: "whatsapp",
            onClick: (e) => {
              e.stopPropagation();
              onClickWhatsapp(item);
            },
            children: /* @__PURE__ */ jsx("span", { className: "flex-none flex", children: /* @__PURE__ */ jsxs(
              "svg",
              {
                xmlns: "http://www.w3.org/2000/svg",
                height: "20px",
                width: "20px",
                viewBox: "0 0 14 14",
                className: "flex-none",
                children: [
                  /* @__PURE__ */ jsx(
                    "path",
                    {
                      fillRule: "evenodd",
                      clipRule: "evenodd",
                      d: "M11.4554 2.74662C12.0268 3.31986 12.4688 3.96925 12.7813 4.69477C13.0938 5.42028 13.25 6.17715 13.25 6.96537C13.25 8.04021 12.9777 9.03443 12.4331 9.94805C11.8884 10.8617 11.1563 11.5872 10.2366 12.1246C9.31698 12.662 8.32145 12.9307 7.25001 12.9307C6.23215 12.9307 5.28572 12.6889 4.41072 12.2052L1.25 13.0382L2.10714 9.94805C1.57143 9.03443 1.30357 8.04021 1.30357 6.96537C1.30357 5.89053 1.57143 4.8963 2.10714 3.98268C2.64286 3.06907 3.36608 2.34355 4.27679 1.80613C5.18751 1.26871 6.17858 1 7.25001 1C8.03573 1 8.7902 1.15227 9.51341 1.45681C10.2366 1.76135 10.884 2.19128 11.4554 2.74662Z",
                      stroke: "currentColor",
                      fill: "transparent"
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    "path",
                    {
                      fillRule: "evenodd",
                      clipRule: "evenodd",
                      d: "M10.4221 8.40265L10.5163 8.46565C10.6629 8.52865 10.7466 8.58115 10.7675 8.62315C10.7885 8.66515 10.7937 8.77015 10.7832 8.93814C10.7728 9.10614 10.7309 9.27939 10.6576 9.45789C10.5844 9.63638 10.4169 9.81488 10.1552 9.99338C9.89358 10.1719 9.66855 10.2716 9.48015 10.2926C9.18709 10.3346 8.90449 10.3346 8.63236 10.2926C8.31837 10.2296 7.89971 10.0826 7.37638 9.85163C6.3088 9.38964 5.33541 8.52865 4.45623 7.26867L4.39343 7.20567C3.9329 6.55468 3.70264 5.95619 3.70264 5.4102C3.70264 4.86421 3.89103 4.39171 4.26783 3.99272L4.29923 3.96122C4.44576 3.81422 4.60276 3.74072 4.77022 3.74072H5.14701C5.23075 3.74072 5.29878 3.75647 5.35111 3.78797C5.40344 3.81947 5.45054 3.89822 5.49241 4.02422L6.0262 5.3157C6.089 5.4417 6.09947 5.54669 6.0576 5.63069C5.95294 5.84069 5.81687 6.02969 5.64941 6.19769C5.56567 6.28168 5.51334 6.34993 5.49241 6.40243C5.47148 6.45493 5.49241 6.51268 5.55521 6.57568C5.84827 7.12167 6.19367 7.55216 6.59139 7.86716C6.88445 8.09816 7.32405 8.36065 7.91017 8.65465C8.07764 8.73865 8.20323 8.72815 8.28697 8.62315C8.5591 8.30815 8.74749 8.07716 8.85216 7.93016C8.89402 7.84616 8.94636 7.80416 9.00916 7.80416C9.07195 7.80416 9.14522 7.81466 9.22895 7.83566C9.39642 7.89866 9.79414 8.08766 10.4221 8.40265Z",
                      fill: "currentColor"
                    }
                  )
                ]
              }
            ) })
          }
        )
      ] })
    ] })
  ] });
};
var PostEnquiryBulk = ({
  isBulkEnquiry,
  isMortgage,
  isNewLaunch = false,
  isVerified,
  items,
  onClickPhone,
  onClickWhatsapp,
  title: propsTitle,
  lang
}) => {
  const title = () => {
    return lang === "en" ? "Request Successful" : "Permintaan Berhasil";
  };
  let subtitle = lang === "en" ? "Please wait for agent(s) to contact you" : "Tunggu Agen akan menghubungi Anda";
  if (isNewLaunch) subtitle = lang === "en" ? "Please wait for developer(s) to contact you" : "Tunggu Pengembang akan menghubungi Anda";
  if (isMortgage) subtitle = lang === "en" ? "Please wait for Agent and Rumah123 Team to contact you" : "Tunggu Agen dan Tim Rumah123 akan menghubungi Anda";
  if (isBulkEnquiry) subtitle = propsTitle || "";
  return /* @__PURE__ */ jsxs("div", { className: "w-full flex flex-col gap-4 max-h-[500px]", children: [
    /* @__PURE__ */ jsx("div", { className: "text-2xl font-semibold w-full text-center", children: title() }),
    /* @__PURE__ */ jsx("p", { className: "text-sm w-full text-center", children: subtitle }),
    /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-4", children: items.map((item) => {
      const hasPhone = item.phoneNumbers && item.phoneNumbers.length > 0 ? item.phoneNumbers[0] : void 0;
      const hasWhatsapp = item.whatsappNumbers && item.whatsappNumbers.length > 0 ? item.whatsappNumbers[0] : void 0;
      return /* @__PURE__ */ jsx(
        PostEnquiryBulkCard,
        {
          item,
          isVerified,
          hasPhone,
          hasWhatsapp,
          onClickPhone,
          onClickWhatsapp
        },
        `Bulk Enquiry ${item.uuid}`
      );
    }) })
  ] });
};
var NameAvatar = ({
  name,
  size = 48,
  agentImageUrl,
  children,
  style
}) => {
  if (agentImageUrl && /\.(jpg|png|webp)$/i.test(agentImageUrl) && !agentImageUrl.includes("r123-grey")) {
    return /* @__PURE__ */ jsx(Fragment, { children });
  }
  const getInitials = (userName) => {
    const nameParts = userName.trim().split(" ");
    if (nameParts.length === 1) {
      return nameParts[0][0]?.toUpperCase();
    }
    if (nameParts.length === 2) {
      return nameParts[0][0]?.toUpperCase() + nameParts[1][0]?.toUpperCase();
    }
    return nameParts[0][0]?.toUpperCase() + nameParts[nameParts.length - 1][0]?.toUpperCase();
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: "flex items-center justify-center rounded-full text-white font-bold",
      style: {
        backgroundColor: "#3A6ACD",
        fontSize: size / 2.54,
        height: size,
        width: size,
        ...style || {}
      },
      children: getInitials(name)
    }
  );
};

export { Avatar_default as Avatar, Button, Checkbox_default as Checkbox, CheckboxItem_default as CheckboxItem, Chip_default as Chip, ChipOfficialIcon_default as ChipOfficialIcon, ChipSelection_default as ChipSelection, Dialog, DialogSuccess, DialogWithHeader, Input, Label, LabelWithFlagSuccess, Loader, ModalRoomTypeFilter, MortgageDialogConsent, NameAvatar, PostEnquiryBulk, PostEnquiryBulkCard, PostEnquirySuggestion, PostEnquirySuggestionCard, ReactPortalComponent, ScrollableWrapper_default as ScrollableWrapper, Select, Slider, WidgetInternalLink, WidgetInternalLinkAccordion, WidgetInternalLinkBasic, WidgetInternalLinkItem, deleteCookie, getCookie, setCookie, tailwind_exports as tailwindHelpers, useAsyncModal, useCookieStateWithEncryption, useCookiesState, useIntersectionObserver, useMediaQuery, useOnClickOutside, useStorageState, useStorageStateWithEncryption };
